<template>
  <div class="schedule-view">
    <div class="schedule-page-layout">
      <div class="mobile-view-tabs" role="tablist" aria-label="スケジュール表示">
        <button
          ref="mobileListTab"
          id="schedule-list-tab"
          type="button"
          role="tab"
          :class="['mobile-view-tab', { active: activeMobilePanel === 'list' }]"
          :aria-selected="activeMobilePanel === 'list'"
          :tabindex="activeMobilePanel === 'list' ? 0 : -1"
          aria-controls="schedule-list-panel"
          @click="activeMobilePanel = 'list'"
          @keydown="handleMobileTabKeydown"
        >
          <IconSvg name="list-solid-full" size="sm" />
          一覧
        </button>
        <button
          ref="mobileMapTab"
          id="schedule-map-tab"
          type="button"
          role="tab"
          :class="['mobile-view-tab', { active: activeMobilePanel === 'map' }]"
          :aria-selected="activeMobilePanel === 'map'"
          :tabindex="activeMobilePanel === 'map' ? 0 : -1"
          aria-controls="schedule-map-panel"
          @click="activeMobilePanel = 'map'"
          @keydown="handleMobileTabKeydown"
        >
          <IconSvg name="map-solid-full" size="sm" />
          マップ
        </button>
      </div>

      <main
        id="schedule-list-panel"
        class="schedule-left-pane"
        :class="{ 'mobile-panel-inactive': activeMobilePanel !== 'list' }"
        :role="isMobileViewport ? 'tabpanel' : undefined"
        :aria-labelledby="isMobileViewport ? 'schedule-list-tab' : undefined"
      >
        <div class="schedule-container">
      <!-- フィルターセクション -->
      <div class="action-bar">
        <!-- モバイル: フィルタートグルボタン -->
        <button
          class="filter-toggle-btn"
          @click="filterSidebarOpen = !filterSidebarOpen"
        >
          <IconSvg name="list-solid-full" size="sm" />
          フィルター
          <span v-if="hasActiveFilters" class="filter-active-dot"></span>
        </button>

        <!-- フィルターオーバーレイ（モバイル） -->
        <div
          v-if="filterSidebarOpen"
          class="filter-overlay"
          @click="filterSidebarOpen = false"
        ></div>

        <!-- フィルターサイドバー -->
        <div :class="['filters-sidebar', { 'is-open': filterSidebarOpen }]">
          <div class="filters-sidebar-header">
            <span>フィルター</span>
            <button
              class="sidebar-close-btn"
              @click="filterSidebarOpen = false"
            >
              ×
            </button>
          </div>
          <div class="filters-container">
            <!-- メンバーフィルター -->
            <div class="filter-group">
              <label class="filter-label">
                <IconSvg name="user-solid-full" size="xs" />
                メンバー
              </label>
              <select v-model="selectedMemberFilter" class="filter-select">
                <option value="all">全員</option>
                <option
                  v-for="member in shioriMembers"
                  :key="member.MEMBER_CODE"
                  :value="member.MEMBER_CODE"
                >
                  {{ member.MEMBER_NAME }}
                </option>
              </select>
            </div>

            <!-- 予定の種類フィルター -->
            <div class="filter-group">
              <label class="filter-label">
                <IconSvg name="list-solid-full" size="xs" />
                予定の種類
              </label>
              <select v-model="selectedCategoryFilter" class="filter-select">
                <option value="all">全て</option>
                <option
                  v-for="cat in scheduleCategoryOptions"
                  :key="cat.CATEGORY_CDV"
                  :value="cat.CATEGORY_CDV"
                >
                  {{ cat.CATEGORY_NMV }}
                </option>
              </select>
            </div>

            <!-- 旅程への表示フィルター -->
            <div class="filter-group">
              <label class="filter-label">
                <IconSvg name="list-solid-full" size="xs" />
                旅程への表示
              </label>
              <select v-model="selectedNeedFilter" class="filter-select">
                <option value="all">全て</option>
                <option value="needed">旅程に含む</option>
                <option value="not-needed">未確定</option>
              </select>
            </div>

            <!-- 予約状況フィルター -->
            <div class="filter-group">
              <label class="filter-label">
                <IconSvg name="check-solid-full" size="xs" />
                予約状況
              </label>
              <select v-model="selectedReservationFilter" class="filter-select">
                <option value="all">全て</option>
                <option value="need-reservation">予約が必要</option>
                <option value="reservation-done">予約完了</option>
                <option value="reservation-pending">予約未完了</option>
                <option value="no-reservation">予約不要</option>
              </select>
            </div>

            <!-- 費用フィルター -->
            <div class="filter-group">
              <label class="filter-label">
                <IconSvg name="yen-sign-solid-full" size="xs" />
                費用
              </label>
              <select v-model="selectedCostFilter" class="filter-select">
                <option value="all">全て</option>
                <option value="with-cost">費用あり</option>
                <option value="no-cost">費用なし</option>
              </select>
            </div>

            <!-- フィルタークリアボタン -->
            <button
              v-if="hasActiveFilters"
              @click="clearFilters"
              class="clear-filters-btn"
              title="フィルターをクリア"
            >
              <IconSvg name="trash-solid-full" size="sm" />
              クリア
            </button>
          </div>
        </div>
      </div>

      <!-- ローディング表示 -->
      <div v-if="scheduleStore.isLoading" class="loading-state">
        <div class="spinner"></div>
        <p>読み込み中...</p>
      </div>

      <!-- スケジュールがない場合 -->
      <div v-else-if="groupedSchedules.length === 0" class="empty-state">
        <div class="empty-icon">
          <IconSvg name="calendar-solid-full" size="xl" />
        </div>
        <h3 class="empty-title">スケジュールがまだありません</h3>
        <p class="empty-description">
          上のボタンから新しいスケジュールを作成しましょう！
        </p>
      </div>

      <!-- 日程タブ -->
      <div
        v-if="!scheduleStore.isLoading && groupedSchedules.length > 0"
        class="schedule-tabs"
      >
        <div class="tabs-header">
          <button
            v-for="(day, index) in groupedSchedules"
            :key="index"
            :class="['tab-button', { active: activeDay === index }]"
            @click="activeDay = index"
          >
            {{ `${index + 1}日目` }}
            <span v-if="day.items.length > 0" class="tab-count">{{
              day.items.length
            }}</span>
          </button>
        </div>

        <!-- スケジュール一覧 -->
        <!-- スケジュール一覧 -->
        <div class="tabs-content">
          <div
            v-for="(day, index) in groupedSchedules"
            :key="index"
            v-show="activeDay === index"
            class="day-content"
          >
            <div class="day-header-bar">
              <h3 class="day-title">
                <span class="day-date-label">{{ day.dateStr }}</span>
              </h3>
              <WeatherForecast
                v-if="weatherLocation"
                :location="weatherLocation"
                :date="day.dateObj"
                :start-date="day.dateObj"
                mode="badge"
                class="day-weather"
              />
              <button
                v-if="canWrite"
                @click="openCreateModalForDate(day.dateObj)"
                class="btn btn-primary btn-sm"
              >
                <IconSvg name="plus-solid-full" size="sm" /><span
                  class="btn-add-label"
                >
                  スケジュール追加</span
                >
              </button>
            </div>

            <div v-if="day.items.length === 0" class="no-items">
              この日のスケジュールはまだありません
            </div>
            <div v-else class="schedule-day-layout">
              <div class="schedule-items">
                <div
                  v-for="item in day.items"
                  :key="`${item.LINE_NON}-${day.dateObj}`"
                  :data-schedule-id="item.LINE_NON"
                  :class="[
                    'schedule-item',
                    {
                      'menu-open': openKebabItemId === item.LINE_NON,
                      'current-live': isCurrentLiveSchedule(item),
                    },
                  ]"
                  @click="handleScheduleItemClick($event, item)"
                >
                <div class="item-pin-number">
                  {{ item.MAP_PIN_NUMBER }}
                </div>
                <div class="item-icon">
                  <span class="category-icon">
                    <IconSvg
                      :name="getCategoryIcon(item.CATEGORY_CDV)"
                      size="md"
                    />
                  </span>
                </div>
                <div class="item-time">
                  <template v-if="!item.IS_MULTI_DAY_CONTINUATION">
                    <input
                      class="direct-edit-input direct-time-input"
                      type="time"
                      :data-inline-edit-id="item.LINE_NON"
                      :value="
                        inlineEdit.lineNon === item.LINE_NON
                          ? inlineEdit.startTime
                          : item.DISPLAY_START_TIME || ''
                      "
                      :disabled="!canWrite || inlineEdit.savingLineNon === item.LINE_NON"
                      aria-label="開始時刻"
                      @focus="startInlineEdit(item)"
                      @input="updateInlineStartTime(item, $event.target.value)"
                      @blur="handleInlineBlur($event, item)"
                      @keydown.enter.prevent="$event.currentTarget.blur()"
                    />
                    <span
                      v-if="getScheduleEndTimeLabel(item)"
                      class="end-time-label"
                    >
                      〜 {{ getScheduleEndTimeLabel(item) }}
                    </span>
                  </template>
                  <span v-else class="time-badge continuation-time-badge">
                    {{ getScheduleTimeLabel(item) }}
                  </span>
                </div>
                <div class="item-content">
                  <div class="item-title-row">
                    <input
                      class="item-title direct-edit-input direct-title-input"
                      type="text"
                      maxlength="255"
                      :data-inline-edit-id="item.LINE_NON"
                      :value="
                        inlineEdit.lineNon === item.LINE_NON
                          ? inlineEdit.title
                          : item.SUBJECT_NMV || ''
                      "
                      :disabled="!canWrite || inlineEdit.savingLineNon === item.LINE_NON"
                      aria-label="予定名"
                      @focus="startInlineEdit(item)"
                      @input="updateInlineTitle(item, $event.target.value)"
                      @blur="handleInlineBlur($event, item)"
                      @keydown.enter.prevent="$event.currentTarget.blur()"
                    />
                    <div
                      v-if="item.MEMBERS && item.MEMBERS.length > 0"
                      class="members-avatars"
                      aria-label="参加メンバー"
                    >
                      <div
                        v-for="(member, idx) in item.MEMBERS"
                        :key="idx"
                        class="member-avatar"
                        :title="member.MEMBER_NAME"
                      >
                        <img
                          v-if="member.PROFILE_IMAGE_BASE64"
                          :src="member.PROFILE_IMAGE_BASE64"
                          :alt="member.MEMBER_NAME"
                          class="avatar-img"
                          loading="lazy"
                          decoding="async"
                        />
                        <IconSvg
                          v-else
                          name="user-solid-full"
                          size="xs"
                          class="avatar-placeholder"
                        />
                      </div>
                    </div>
                  </div>
                  <p
                    v-if="
                      inlineEdit.lineNon === item.LINE_NON &&
                      inlineEdit.error
                    "
                    class="inline-edit-error"
                  >
                    {{ inlineEdit.error }}
                  </p>
                  <p v-if="item.REMARKS" class="item-description">
                    {{ item.REMARKS }}
                  </p>
                  <div class="item-meta">
                    <span
                      v-if="isCurrentLiveSchedule(item)"
                      class="meta-tag live-current-tag"
                    >
                      <IconSvg name="circle-info-solid-full" size="xs" />
                      進行中
                    </span>
                    <span
                      v-if="item.NEED_FLG === '0'"
                      class="meta-tag not-needed-tag"
                    >
                      未確定
                    </span>
                    <div
                      v-if="
                        item.RESERVATION_FLG === '1' || item.DISPLAY_COST
                      "
                      class="status-cost-row"
                    >
                      <span
                        v-if="
                          item.RESERVATION_FLG === '1' &&
                          item.RESERVATION_DONE_FLG !== '1'
                        "
                        class="meta-tag reservation-pending-tag"
                      >
                        <IconSvg name="check-solid-full" size="xs" />
                        予約未完了
                      </span>
                      <a
                        v-if="
                          item.RESERVATION_FLG === '1' &&
                          item.RESERVATION_DONE_FLG !== '1' &&
                          isRakutenTravelEligible(item)
                        "
                        :href="getReservationUrl(item)"
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        class="meta-link reservation-booking-link"
                        @click.stop
                      >
                        <IconSvg name="link-solid-full" size="xs" />
                        予約する
                      </a>
                      <span
                        v-else-if="
                          item.RESERVATION_FLG === '1' &&
                          item.RESERVATION_DONE_FLG !== '1'
                        "
                        class="meta-tag reservation-preparing-tag"
                      >
                        Coming soon
                      </span>
                      <span
                        v-if="
                          item.RESERVATION_FLG === '1' &&
                          item.RESERVATION_DONE_FLG === '1'
                        "
                        class="meta-tag reservation-tag"
                      >
                        <IconSvg name="check-solid-full" size="xs" /> 予約完了
                      </span>
                      <span v-if="item.DISPLAY_COST" class="meta-tag cost-tag">
                        <IconSvg name="yen-sign-solid-full" size="xs" /> 一人:
                        {{
                          formatCost(
                            Math.ceil(
                              item.DISPLAY_COST / getCostParticipantCount(item),
                            ),
                          )
                        }}
                        {{ formatCurrencyUnit(item.CURRENCY) }}
                      </span>
                    </div>
                    <div
                      v-if="
                        item.RESERVATION_FLG === '1' &&
                        item.RESERVATION_DONE_FLG !== '1' &&
                        isRakutenTravelEligible(item)
                      "
                      class="reservation-provider-strip"
                    >
                      <a
                        v-for="provider in getReservationProviders(item)"
                        :key="provider.name"
                        :href="provider.href"
                        target="_blank"
                        rel="noopener noreferrer sponsored"
                        class="reservation-provider-chip"
                        @click.stop
                      >
                        <span class="reservation-provider-name">
                          {{ provider.name }}
                        </span>
                        <span class="reservation-provider-price">
                          {{ provider.priceLabel }}
                        </span>
                      </a>
                    </div>
                    <a
                      v-if="item.RESERVATION_DOCUMENT_URL"
                      :href="item.RESERVATION_DOCUMENT_URL"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="meta-link reservation-document-link"
                      @click.stop
                    >
                      <IconSvg name="link-solid-full" size="xs" />
                      {{ item.RESERVATION_DOCUMENT_NAME || "予約書類" }}
                    </a>
                    <a
                      v-if="item.URL_NMV"
                      :href="item.URL_NMV"
                      target="_blank"
                      class="meta-link"
                    >
                      <IconSvg name="link-solid-full" size="xs" /> リンク
                    </a>
                    <a
                      v-if="getMapUrl(item)"
                      :href="getMapUrl(item)"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="地図を開く（新しいタブ）"
                      class="meta-link map-link"
                    >
                      <IconSvg name="map-solid-full" size="xs" /> 地図を開く
                    </a>
                  </div>
                </div>
                  <div v-if="canWrite" class="item-actions">
                    <button
                      @click="createChecklistFromSchedule(item)"
                      class="action-btn checklist-btn desktop-action"
                      title="ToDo作成"
                    >
                      <IconSvg name="check-solid-full" size="sm" />
                    </button>
                    <button
                      @click="copySchedule(item)"
                      class="action-btn copy-btn desktop-action"
                      title="コピー"
                    >
                      <IconSvg name="copy-solid-full" size="sm" />
                    </button>
                    <button
                      @click="editSchedule(item)"
                      class="action-btn edit-btn desktop-action"
                      title="編集"
                    >
                      <IconSvg name="pen-to-square-solid-full" size="sm" />
                    </button>
                    <button
                      @click="deleteSchedule(item.LINE_NON)"
                      class="action-btn delete-btn desktop-action"
                      title="削除"
                    >
                      <IconSvg name="trash-solid-full" size="sm" />
                    </button>
                    <!-- モバイル: 3点ケバブメニュー -->
                    <div class="kebab-wrapper mobile-action">
                      <button
                        class="action-btn kebab-btn"
                        aria-label="メニュー"
                        @click.stop="
                          openKebabItemId =
                            openKebabItemId === item.LINE_NON
                              ? null
                              : item.LINE_NON
                        "
                        title="メニュー"
                      >
                        ⋮
                      </button>
                      <div
                        v-if="openKebabItemId === item.LINE_NON"
                        class="kebab-menu"
                        @click.stop
                      >
                        <button
                          @click="
                            createChecklistFromSchedule(item);
                            openKebabItemId = null;
                          "
                          class="kebab-item"
                        >
                          チェックリスト作成
                        </button>
                        <button
                          @click="
                            copySchedule(item);
                            openKebabItemId = null;
                          "
                          class="kebab-item"
                        >
                          コピー
                        </button>
                        <button
                          @click="
                            editSchedule(item);
                            openKebabItemId = null;
                          "
                          class="kebab-item"
                        >
                          編集
                        </button>
                        <button
                          @click="
                            deleteSchedule(item.LINE_NON);
                            openKebabItemId = null;
                          "
                          class="kebab-item kebab-delete"
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        </div>
      </main>

      <aside
        id="schedule-map-panel"
        class="schedule-map-pane"
        :class="{ 'mobile-panel-inactive': activeMobilePanel !== 'map' }"
        :role="isMobileViewport ? 'tabpanel' : undefined"
        :aria-labelledby="isMobileViewport ? 'schedule-map-tab' : undefined"
      >
        <div class="schedule-map-panel">
          <div class="schedule-map-header">
            <h2 class="schedule-map-title">
              <IconSvg name="map-solid-full" size="md" />
              {{ activeDayMapTitle }}
            </h2>
            <span class="schedule-map-count">
              {{ activeDayItemsWithCoordinates.length }}件
            </span>
          </div>
          <div class="schedule-map-body">
            <MapComponent
              :key="`schedule-map-${activeDay}`"
              :items="activeDayItemsWithCoordinates"
              item-key-field="LINE_NON"
              :focused-item-key="focusedScheduleMapItemKey"
              :center="activeDayMapCenter"
              :zoom="activeDayMapZoom"
            />
            <p
              v-if="activeDayItemsWithCoordinates.length === 0"
              class="schedule-map-empty"
            >
              この日付には地図に表示できる場所がありません。スケジュールを追加・編集して場所候補を選択し、住所と緯度経度が保存されるとピンが表示されます。
            </p>
          </div>
          <div class="route-summary-panel">
            <div>
              <span class="assist-kicker">スポット追加時の地図/移動時間</span>
              <h3>{{ activeDayRouteSummary.title }}</h3>
              <p>{{ activeDayRouteSummary.description }}</p>
              <div
                v-if="activeDayRouteSummary.timingChecks.length"
                class="route-timing-checks"
                aria-label="区間ごとの移動時間確認"
              >
                <span
                  v-for="check in activeDayRouteSummary.timingChecks"
                  :key="check.key"
                  :class="[
                    'route-timing-check',
                    check.unconfirmed
                      ? 'unconfirmed'
                      : check.needsAttention
                        ? 'needs-attention'
                        : 'has-margin',
                  ]"
                >
                  {{ check.label }}
                </span>
              </div>
            </div>
            <div class="route-summary-actions">
              <a
                v-if="activeDayRouteSummary.mapUrl"
                :href="activeDayRouteSummary.mapUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="route-summary-link"
              >
                Google Mapsでルート確認
              </a>

            </div>
          </div>
        </div>
      </aside>
    </div>

    <section class="planning-assist-panel">
      <div v-if="canWrite" class="assist-section template-section">
        <div class="assist-heading">
          <div>
            <span class="assist-kicker">旅程テンプレート</span>
            <h2>よくある予定をすばやく追加</h2>
          </div>
          <span class="assist-count">{{ scheduleTemplates.length }}件</span>
        </div>
        <div class="template-grid">
          <button
            v-for="template in scheduleTemplates"
            :key="template.id"
            type="button"
            class="template-card"
            @click="openTemplateModal(template)"
          >
            <span class="template-icon">
              <IconSvg :name="template.icon" size="sm" />
            </span>
            <strong>{{ template.title }}</strong>
            <span>{{ template.timeLabel }}</span>
          </button>
        </div>
      </div>

      <div v-if="canWrite" class="assist-section paste-import-section">
        <div class="assist-heading">
          <div>
            <span class="assist-kicker">旅程テキスト取り込み</span>
            <h2>メモから下書きを作る</h2>
          </div>
          <span class="assist-count">{{ itineraryDrafts.length }}件</span>
        </div>
        <div class="paste-import-body">
          <label class="paste-import-label" for="itinerary-paste-input">
            予定メモ
          </label>
          <textarea
            id="itinerary-paste-input"
            v-model="itineraryPasteText"
            class="paste-import-textarea"
            aria-label="予定貼り付け"
            rows="4"
            placeholder="2026-08-10 09:00-10:00 浅草寺 観光&#10;1日目 12:00-13:00 築地ランチ 食事"
          ></textarea>
          <div class="paste-import-actions">
            <button
              type="button"
              class="btn btn-primary btn-sm paste-parse-btn"
              @click="parseItineraryText"
            >
              <IconSvg name="list-solid-full" size="sm" />
              下書きに変換
            </button>
            <span v-if="itinerarySkippedRows.length > 0" class="paste-skip-count">
              未取込 {{ itinerarySkippedRows.length }}行
            </span>
          </div>
        </div>
        <div v-if="itineraryDrafts.length > 0" class="itinerary-draft-list">
          <article
            v-for="draft in itineraryDrafts"
            :key="draft.id"
            class="itinerary-draft-card"
          >
            <div class="draft-card-main">
              <span class="draft-date">{{ draft.startDate }}</span>
              <strong>{{ draft.title }}</strong>
              <span class="draft-time">
                {{ draft.startTime }}<template v-if="draft.endTime">-{{ draft.endTime }}</template>
              </span>
              <a
                v-if="draft.url"
                :href="draft.url"
                class="draft-link"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                リンク
              </a>
            </div>
            <div class="draft-card-actions">
              <button
                type="button"
                class="draft-action-btn draft-open-btn"
                @click="openDraftModal(draft)"
              >
                編集して追加
              </button>
              <button
                type="button"
                class="draft-icon-btn"
                :aria-label="draft.title + ' の下書きを削除'"
                @click="removeItineraryDraft(draft.id)"
              >
                <IconSvg name="trash-solid-full" size="sm" />
              </button>
            </div>
          </article>
        </div>
        <div
          v-if="itinerarySkippedRows.length > 0"
          class="itinerary-skipped-list"
          aria-live="polite"
        >
          <strong>取り込めなかった行</strong>
          <ul>
            <li v-for="row in itinerarySkippedRows" :key="row.id">
              {{ row.text }}<span>{{ row.reason }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="canWrite" class="assist-section reservation-section">
        <div class="assist-heading">
          <div>
            <span class="assist-kicker">予約情報管理</span>
            <h2>未完了の予約を確認</h2>
          </div>
          <span
            class="assist-count"
            :class="{ warning: reservationSummary.pending > 0 }"
          >
            {{ reservationSummary.pending }}件
          </span>
        </div>
        <div v-if="reservationWatchItems.length > 0" class="reservation-list">
          <button
            v-for="item in reservationWatchItems"
            :key="item.LINE_NON"
            type="button"
            class="reservation-row"
            @click="editSchedule(item)"
          >
            <span class="reservation-row-date">
              {{ formatDateShort(item.START_YMD) }}
            </span>
            <span class="reservation-row-main">
              <strong>{{ item.SUBJECT_NMV }}</strong>
              <small>{{ item.RESERVATION_DONE_FLG === "1" ? "予約完了" : "予約未完了" }}</small>
            </span>
            <a
              v-if="item.RESERVATION_DONE_FLG !== '1' && isRakutenTravelEligible(item)"
              :href="getReservationUrl(item)"
              target="_blank"
              rel="noopener noreferrer sponsored"
              class="reservation-row-link"
              @click.stop
            >
              予約
            </a>
          </button>
        </div>
        <p v-else class="assist-empty">
          予約が必要な予定はまだありません。
        </p>
      </div>

    </section>

    <!-- フローティングボタン（スケジュール作成） -->
    <button
      v-if="canWrite && !showModal"
      @click="openCreateModal"
      class="floating-btn"
      title="スケジュール作成"
    >
      <span class="floating-btn-icon">
        <IconSvg name="plus-solid-full" size="md" />
      </span>
    </button>

    <!-- スケジュール作成モーダル -->
    <CreateScheduleModal
      v-if="canWrite"
      v-model="showModal"
      :shioriId="shioriId"
      :editMode="editMode"
      :scheduleData="editingSchedule"
      :initialDate="initialDate"
      :prefillData="prefillScheduleData"
      @create="onScheduleCreated"
      @update="onScheduleUpdated"
    />
  </div>
</template>

<script>
import {
  ref,
  computed,
  onMounted,
  onUnmounted,
  reactive,
  nextTick,
  watch,
} from "vue";
import { useRoute } from "vue-router";
import CreateScheduleModal from "@/components/CreateScheduleModal.vue";
import MapComponent from "@/components/MapComponent.vue";
import IconSvg from "@/components/IconSvg.vue";
import WeatherForecast from "@/components/WeatherForecast.vue";
import { useScheduleStore } from "@/store/schedule-store.js";
import { useShioriStore } from "@/store/shiori-store.js";
import { useShioriMemberStore } from "@/store/shiori-member-store.js";
import { useChecklistStore } from "@/store/checklist-store";
import { useAuthStore } from "@/store/authStore.js";
import { useScheduleCategoryStore } from "@/store/schedule-category-store.js";
import { API_URL } from "@/config/api.js";
import { getTripLiveState } from "@/utils/trip-live.js";

export default {
  name: "ScheduleView",
  components: {
    CreateScheduleModal,
    MapComponent,
    IconSvg,
    WeatherForecast,
  },
  props: {
    canWrite: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const route = useRoute();
    const scheduleStore = useScheduleStore();
    const shioriStore = useShioriStore();
    const shioriMemberStore = useShioriMemberStore();
    const checklistStore = useChecklistStore();
    const authStore = useAuthStore();
    const scheduleCategoryStore = useScheduleCategoryStore();
    const showModal = ref(false);
    const shioriId = ref(route.params.id || "");
    const openDays = reactive({});
    const editMode = ref(false);
    const editingSchedule = ref(null);
    const prefillScheduleData = ref(null);
    const scrollYBeforeEdit = ref(0);
    const selectedMemberFilter = ref("all"); // メンバーフィルター
    const selectedCategoryFilter = ref("all"); // カテゴリフィルター
    const selectedNeedFilter = ref("all"); // 要不要フィルター
    const selectedReservationFilter = ref("all"); // 予約状況フィルター
    const selectedCostFilter = ref("all"); // 費用フィルター
    const initialDate = ref(null); // 初期設定用の日付
    const activeDay = ref(0); // アクティブな日程タブ
    const activeMobilePanel = ref("list");
    const mobileListTab = ref(null);
    const mobileMapTab = ref(null);
    const isMobileViewport = ref(false);
    let mobileViewportQuery = null;
    const liveNow = ref(new Date());
    let liveTimerId = null;
    const filterSidebarOpen = ref(false); // フィルターサイドバー表示
    const openKebabItemId = ref(null); // ケバブメニュー表示アイテムID
    const focusedScheduleMapItemKey = ref(null);
    const inlineEdit = reactive({
      lineNon: null,
      title: "",
      startTime: "",
      endTime: "",
      endDate: "",
      originalTitle: "",
      originalStartTime: "",
      originalEndTime: "",
      originalEndDate: "",
      savingLineNon: null,
      error: "",
    });
    const itineraryPasteText = ref("");
    const itineraryDrafts = ref([]);
    const itinerarySkippedRows = ref([]);
    let itineraryDraftSeq = 0;

    const scheduleTemplates = [
      {
        id: "hotel-checkin",
        title: "ホテルチェックイン",
        timeLabel: "15:00-16:00",
        icon: "bed-solid-full",
        category: "hotel",
        startTime: "15:00",
        endTime: "16:00",
        reservation: true,
        remarks: "予約名、予約番号、チェックイン方法をメモしておくと安心です。",
      },
      {
        id: "lunch",
        title: "ランチ",
        timeLabel: "12:00-13:00",
        icon: "utensils-solid-full",
        category: "restaurant",
        startTime: "12:00",
        endTime: "13:00",
        reservation: false,
        remarks: "候補店、混雑時の代替案、支払い方法をメモできます。",
      },
      {
        id: "sightseeing",
        title: "観光スポット",
        timeLabel: "10:00-11:30",
        icon: "building-columns-solid-full",
        category: "sightseeing",
        startTime: "10:00",
        endTime: "11:30",
        reservation: false,
        remarks: "営業時間、入場料、雨天時の代替案をメモできます。",
      },
      {
        id: "transfer",
        title: "移動",
        timeLabel: "09:00-09:30",
        icon: "train-subway-solid-full",
        category: "movement",
        startTime: "09:00",
        endTime: "09:30",
        transportation: "電車",
        reservation: false,
        remarks: "乗換、集合場所、チケット種別をメモできます。",
      },
      {
        id: "activity",
        title: "体験・アクティビティ",
        timeLabel: "14:00-16:00",
        icon: "person-walking-solid-full",
        category: "activity",
        startTime: "14:00",
        endTime: "16:00",
        reservation: true,
        remarks: "集合時間、予約番号、持ち物、キャンセル期限をメモできます。",
      },
    ];

    // アクティブなフィルターがあるかチェック
    const hasActiveFilters = computed(() => {
      return (
        selectedMemberFilter.value !== "all" ||
        selectedCategoryFilter.value !== "all" ||
        selectedNeedFilter.value !== "all" ||
        selectedReservationFilter.value !== "all" ||
        selectedCostFilter.value !== "all"
      );
    });

    const isDisplayableCategory = (category) => {
      const code = String(category?.CATEGORY_CDV || "").trim();
      const name = String(category?.CATEGORY_NMV || "").trim();
      const hasInvalidChar = [...name].some((char) => {
        const codePoint = char.codePointAt(0);
        return codePoint === 0xfffd || codePoint < 0x20 || codePoint === 0x7f;
      });
      return Boolean(
        code &&
          name &&
          !hasInvalidChar,
      );
    };

    const normalizeCategory = (category) => ({
      ...category,
      CATEGORY_CDV: String(category.CATEGORY_CDV).trim(),
      CATEGORY_NMV: String(category.CATEGORY_NMV).trim(),
    });

    const uniqueCategoriesByCode = (categories) => {
      const categoryMap = new Map();
      categories.forEach((category) => {
        if (!categoryMap.has(category.CATEGORY_CDV)) {
          categoryMap.set(category.CATEGORY_CDV, category);
        }
      });
      return Array.from(categoryMap.values());
    };

    const scheduleCategoryOptions = computed(() => {
      const categories = uniqueCategoriesByCode(scheduleCategoryStore.categoryList
        .filter(isDisplayableCategory)
        .map(normalizeCategory));
      if (!categories.some((category) => category.CATEGORY_CDV === "movement")) {
        categories.unshift({
          CATEGORY_CDV: "movement",
          CATEGORY_NMV: "移動",
          ICON_NMV: "train-subway-solid-full",
        });
      }
      return categories;
    });

    // フィルタークリア
    const clearFilters = () => {
      selectedMemberFilter.value = "all";
      selectedCategoryFilter.value = "all";
      selectedNeedFilter.value = "all";
      selectedReservationFilter.value = "all";
      selectedCostFilter.value = "all";
    };

    // 現在のしおり情報を取得
    const currentShiori = computed(() => {
      return (
        shioriStore.shiori.find((s) => s.SHIORI_NOV === shioriId.value) ||
        shioriStore.sharedShiori.find((s) => s.SHIORI_NOV === shioriId.value)
      );
    });

    // 天気予報用の場所名（DESTINATIONSの最初の目的地、なければしおり名）
    const weatherLocation = computed(() => {
      if (!currentShiori.value) return "";
      try {
        const dest = currentShiori.value.DESTINATIONS;
        if (dest) {
          const parsed = JSON.parse(dest);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed[0];
        }
      } catch {
        // Invalid destination data falls back to the shiori title.
      }
      return currentShiori.value.SUBJECT_NMV || "";
    });

    // しおりメンバーを取得
    const shioriMembers = computed(() => {
      return shioriMemberStore.members[shioriId.value] || [];
    });

    const isInternationalTrip = computed(
      () => currentShiori.value?.DESTINATION_TYPE === "international",
    );

    // 総費用を計算
    const totalCost = computed(() => {
      return scheduleStore.schedules.reduce((sum, schedule) => {
        return sum + (schedule.COST || 0);
      }, 0);
    });

    // ユニークな訪問先を抽出
    const uniqueLocations = computed(() => {
      const locations = schedulesWithCoordinates.value
        .map((s) => s.ADDRESS_NMV || s.SUBJECT_NMV)
        .filter((loc, index, self) => loc && self.indexOf(loc) === index);
      return locations.slice(0, 10); // 最大10件まで表示
    });

    // 日付範囲をフォーマット
    const formatDateRange = (from, to) => {
      if (!from || !to) return "";
      const fromDate = new Date(from);
      const toDate = new Date(to);
      const diffTime = Math.abs(toDate - fromDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return `${formatDate(from)} ～ ${formatDate(to)} (${diffDays}日間)`;
    };

    // 日付フォーマット関数
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      const weekday = weekdays[date.getDay()];
      return `${year}年${String(month).padStart(2, "0")}月${String(
        day,
      ).padStart(2, "0")}日(${weekday})`;
    };

    const formatDateShort = (dateString) => {
      if (!dateString) return "";
      const d = new Date(dateString);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
      return `${m}/${day}(${weekdays[d.getDay()]})`;
    };

    // カテゴリからアイコンを取得
    const getCategoryIcon = (category) => {
      const fromStore = scheduleCategoryStore.cdvToIcon[category];
      if (fromStore) return fromStore;
      // フォールバック（ストア未ロード時）
      const fallback = {
        car: "car-solid",
        train: "train-subway-solid-full",
        plane: "plane-solid-full",
        bus: "bus-solid-full",
        ship: "sailboat-solid-full",
        hotel: "bed-solid-full",
        restaurant: "utensils-solid-full",
        entertainment: "clapperboard-solid-full",
        activity: "person-walking-solid-full",
        sightseeing: "building-columns-solid-full",
        shopping: "bag-shopping-solid-full",
        nature: "tree-solid-full",
        movement: "train-subway-solid-full",
        other: "map-solid-full",
      };
      return fallback[category] || "map-solid-full";
    };

    // 費用をカンマ区切りでフォーマット
    const formatCost = (cost) => {
      if (!cost) return "0";
      return new Intl.NumberFormat("ja-JP").format(cost);
    };

    const formatCurrencyUnit = (currency) => {
      return (currency || "JPY").toUpperCase() === "JPY" ? "円" : currency;
    };

    const getCostParticipantCount = (schedule) => {
      const splitMembers =
        schedule.MEMBERS?.filter(
          (member) => member.SPLIT_PARTICIPATE_FLG === "1",
        ) || [];
      return splitMembers.length || schedule.MEMBERS?.length || 1;
    };

    const getReservationParticipantCount = (schedule) => {
      const scheduleMembers = Array.isArray(schedule?.MEMBERS)
        ? schedule.MEMBERS.filter(Boolean)
        : [];
      return Math.max(scheduleMembers.length || shioriMembers.value.length || 1, 1);
    };

    // 地図アプリのURLを取得
    const getMapUrl = (item) => {
      const placeName = String(
        item?.GOOGLE_PLACE_NMV ?? item?.googlePlaceNmv ?? "",
      ).trim();
      const placeId = String(
        item?.GOOGLE_PLACE_ID ?? item?.googlePlaceId ?? "",
      ).trim();
      const address = String(item?.ADDRESS_NMV ?? "").trim();
      const query = placeName || address;
      if (!query) return null;
      const params = new URLSearchParams({ api: "1", query });
      if (placeName && placeId) params.set("query_place_id", placeId);
      return `https://www.google.com/maps/search/?${params.toString()}`;
    };

    const getReservationUrl = (item) => {
      const keyword =
        item.SUBJECT_NMV ||
        item.ADDRESS_NMV ||
        weatherLocation.value ||
        currentShiori.value?.SUBJECT_NMV ||
        "旅行";

      const url = new URL(
        `${API_URL}/affiliate/rakuten-travel/redirect`,
        window.location.origin,
      );
      url.searchParams.set("keyword", keyword);
      url.searchParams.set("service", getRakutenTravelService(item) || "hotel");
      if (item.ADDRESS_NMV || weatherLocation.value) {
        url.searchParams.set("location", item.ADDRESS_NMV || weatherLocation.value);
      }
      if (item.START_YMD) {
        url.searchParams.set("checkinDate", String(item.START_YMD).slice(0, 10));
      }
      if (item.END_YMD) {
        url.searchParams.set("checkoutDate", String(item.END_YMD).slice(0, 10));
      }
      url.searchParams.set(
        "adultCount",
        String(getReservationParticipantCount(item)),
      );
      return url.toString();
    };

    const getRakutenTravelService = (item) => {
      const category = String(item.CATEGORY_CDV || "").toLowerCase();
      const title = String(item.SUBJECT_NMV || "");
      const isHotel =
        category.includes("hotel") ||
        category.includes("stay") ||
        /ホテル|宿|宿泊|旅館|民宿|ペンション|リゾート|チェックイン/.test(
          title,
        );
      if (isHotel) return "hotel";
      if (
        category.includes("car") ||
        category.includes("rental") ||
        /レンタカー|車|ドライブ/.test(title)
      ) {
        return "rental_car";
      }
      if (
        category.includes("activity") ||
        category.includes("tour") ||
        /アクティビティ|体験|ツアー|観光|チケット|入場/.test(title)
      ) {
        return "activity";
      }
      return null;
    };

    const isRakutenTravelEligible = (item) =>
      !isInternationalTrip.value && Boolean(getRakutenTravelService(item));

    const getReservationProviders = (item) => {
      return [
        {
          name: "楽天トラベル",
          priceLabel: "楽天で確認",
          href: getReservationUrl(item),
        },
      ];
    };

    // 旅行期間の全日付を生成
    const toDateKey = (value) => {
      if (!value) return "";
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return [
          value.getFullYear(),
          String(value.getMonth() + 1).padStart(2, "0"),
          String(value.getDate()).padStart(2, "0"),
        ].join("-");
      }
      const text = String(value).trim();
      const match = text.match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : "";
    };

    const dateKeyToLocalDate = (dateKey) => {
      const [year, month, day] = dateKey.split("-").map(Number);
      return new Date(year, month - 1, day);
    };

    const addDaysToDateKey = (dateKey, offsetDays) => {
      if (!dateKey) return "";
      const date = dateKeyToLocalDate(dateKey);
      if (Number.isNaN(date.getTime())) return "";
      date.setDate(date.getDate() + offsetDays);
      return toDateKey(date);
    };

    const getTripDateRange = () => ({
      from: toDateKey(currentShiori.value?.TRIP_FROM),
      to: toDateKey(currentShiori.value?.TRIP_TO),
    });

    const isDateWithinTrip = (dateKey) => {
      const { from, to } = getTripDateRange();
      if (!dateKey || !from || !to) return false;
      return dateKey >= from && dateKey <= to;
    };

    const normalizeItineraryTime = (value) => {
      if (!value) return "";
      const match = String(value).trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return "";
      const hour = Number(match[1]);
      const minutes = Number(match[2]);
      if (hour > 23 || minutes > 59) return "";
      return `${String(hour).padStart(2, "0")}:${match[2]}`;
    };

    const resolveItineraryDate = (token) => {
      const dateKey = toDateKey(token);
      if (dateKey) return dateKey;
      const dayMatch = String(token || "").match(/^(\d+)日目$/);
      if (!dayMatch || Number(dayMatch[1]) < 1) return "";
      const tripStart = toDateKey(currentShiori.value?.TRIP_FROM);
      if (!tripStart) return "";
      return addDaysToDateKey(tripStart, Number(dayMatch[1]) - 1);
    };

    const inferItineraryCategory = (text) => {
      const normalized = String(text || "").toLowerCase();
      const rules = [
        {
          category: "restaurant",
          pattern: /(食事|ランチ|昼食|朝食|夕食|ディナー|カフェ|グルメ|restaurant|cafe|lunch|dinner)/i,
        },
        {
          category: "hotel",
          pattern: /(ホテル|宿|チェックイン|宿泊|hotel|inn)/i,
        },
        {
          category: "movement",
          pattern: /(移動|電車|列車|新幹線|バス|空港|駅|flight|train|bus|airport|transfer)/i,
        },
        {
          category: "sightseeing",
          pattern: /(観光|寺|神社|城|美術館|博物館|公園|展望|sightseeing|temple|museum|park)/i,
        },
        {
          category: "activity",
          pattern: /(体験|アクティビティ|ツアー|activity|tour)/i,
        },
      ];
      const matched = rules.find((rule) => rule.pattern.test(normalized));
      if (!matched) return "";
      const available = scheduleCategoryOptions.value.some(
        (category) => category.CATEGORY_CDV === matched.category,
      );
      return available || matched.category === "movement" ? matched.category : "";
    };

    const parseItineraryLine = (rawLine) => {
      const original = rawLine.trim();
      if (!original) return null;
      const urlMatch = original.match(/https?:\/\/\S+/i);
      const url = urlMatch ? urlMatch[0].replace(/[、。),）]+$/, "") : "";
      const textWithoutUrl = url ? original.replace(urlMatch[0], " ").trim() : original;
      const lineMatch = textWithoutUrl.match(
        /^(\d{4}-\d{2}-\d{2}|\d+日目)\s+(\d{1,2}:\d{2})(?:\s*[-〜~]\s*(\d{1,2}:\d{2}))?\s+(.+)$/,
      );
      if (!lineMatch) {
        return { skipped: true, reason: "日付・時刻・予定名を読み取れませんでした" };
      }
      const startDate = resolveItineraryDate(lineMatch[1]);
      if (!startDate) {
        return { skipped: true, reason: "日付を旅行期間から解決できませんでした" };
      }
      if (!isDateWithinTrip(startDate)) {
        return { skipped: true, reason: "旅行期間外の日付のため取り込めませんでした" };
      }
      const startTime = normalizeItineraryTime(lineMatch[2]);
      if (!startTime) {
        return { skipped: true, reason: "開始時刻を読み取れませんでした" };
      }
      const endTime = normalizeItineraryTime(lineMatch[3]);
      if (lineMatch[3] && !endTime) {
        return { skipped: true, reason: "終了時刻を読み取れませんでした" };
      }
      let title = lineMatch[4].trim();
      const category = inferItineraryCategory(title);
      title = title
        .replace(/\s+(観光|食事|グルメ|ホテル|宿泊|移動|体験|アクティビティ)$/i, "")
        .trim();
      if (!title) {
        return { skipped: true, reason: "予定名が空でした" };
      }
      const reservationContext = /(予約|確認書|航空券|チケット|booking|reservation|confirmation|ticket)/i.test(
        original,
      );
      return {
        id: `paste-draft-${Date.now()}-${itineraryDraftSeq++}`,
        title,
        startDate,
        startTime,
        endTime,
        category,
        url,
        remarks: `貼り付け元: ${original}`,
        reservation: Boolean(url && reservationContext),
        reservationDone: false,
        reservationDocumentName: url && reservationContext ? `${title} 予約リンク` : "",
        reservationDocumentUrl: url && reservationContext ? url : "",
        needFlg: true,
      };
    };

    const parseItineraryText = () => {
      if (!props.canWrite) return;
      const drafts = [];
      const skipped = [];
      itineraryPasteText.value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .forEach((line, index) => {
          const parsed = parseItineraryLine(line);
          if (!parsed) return;
          if (parsed.skipped) {
            skipped.push({ id: `skip-${index}-${line}`, text: line, reason: parsed.reason });
          } else {
            drafts.push(parsed);
          }
        });
      itineraryDrafts.value = drafts;
      itinerarySkippedRows.value = skipped;
    };

    const openDraftModal = (draft) => {
      if (!props.canWrite) return;
      editMode.value = false;
      editingSchedule.value = null;
      initialDate.value = draft.startDate;
      prefillScheduleData.value = { ...draft };
      showModal.value = true;
    };

    const removeItineraryDraft = (draftId) => {
      if (!props.canWrite) return;
      itineraryDrafts.value = itineraryDrafts.value.filter(
        (draft) => draft.id !== draftId,
      );
    };

    const generateTripDates = () => {
      if (!currentShiori.value) return [];

      const tripFromKey = toDateKey(currentShiori.value.TRIP_FROM);
      const tripToKey = toDateKey(currentShiori.value.TRIP_TO);
      if (!tripFromKey || !tripToKey) return [];

      const dateKeys = scheduleStore.schedules.flatMap((schedule) => [
        toDateKey(schedule.START_YMD),
        toDateKey(schedule.END_YMD || schedule.START_YMD),
      ]);
      const sortedStartKeys = [tripFromKey, ...dateKeys]
        .filter(Boolean)
        .sort();
      const sortedEndKeys = [tripToKey, ...dateKeys].filter(Boolean).sort();
      const minKey = sortedStartKeys[0];
      const maxKey = sortedEndKeys[sortedEndKeys.length - 1];
      const tripFrom = dateKeyToLocalDate(minKey);
      const tripTo = dateKeyToLocalDate(maxKey);
      const dates = [];

      let currentDate = new Date(tripFrom);
      while (currentDate <= tripTo) {
        dates.push(toDateKey(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return dates;
    };

    // 座標を持つスケジュールのみ抽出
    const schedulesWithCoordinates = computed(() => {
      return scheduleStore.schedules.filter(
        (schedule) => hasCoordinates(schedule),
      );
    });

    const getCoordinateValue = (item, uppercaseKey, lowercaseKey) => {
      const value = item?.[uppercaseKey] ?? item?.[lowercaseKey];
      if (
        value === null ||
        value === undefined ||
        (typeof value === "string" && value.trim() === "")
      ) {
        return null;
      }
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    };

    const hasCoordinates = (item) => {
      const lat = getCoordinateValue(item, "LATITUDE", "latitude");
      const lng = getCoordinateValue(item, "LONGITUDE", "longitude");
      return (
        lat !== null &&
        lng !== null &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
      );
    };

    const getItemsWithCoordinates = (items) => items.filter(hasCoordinates);

    const getMapCenter = (items) => {
      const targets = getItemsWithCoordinates(items);
      if (targets.length === 0) return [35.6762, 139.6503];
      const avgLat =
        targets.reduce(
          (sum, item) =>
            sum + getCoordinateValue(item, "LATITUDE", "latitude"),
          0,
        ) /
        targets.length;
      const avgLng =
        targets.reduce(
          (sum, item) =>
            sum + getCoordinateValue(item, "LONGITUDE", "longitude"),
          0,
        ) /
        targets.length;
      return [avgLat, avgLng];
    };

    const getMapZoom = (items) =>
      getItemsWithCoordinates(items).length > 1 ? 12 : 14;

    // 地図の中心座標を計算
    const mapCenter = computed(() => {
      if (schedulesWithCoordinates.value.length === 0) {
        return [35.6762, 139.6503]; // デフォルト: 東京
      }

      const avgLat =
        schedulesWithCoordinates.value.reduce(
          (sum, s) => sum + getCoordinateValue(s, "LATITUDE", "latitude"),
          0,
        ) / schedulesWithCoordinates.value.length;
      const avgLng =
        schedulesWithCoordinates.value.reduce(
          (sum, s) => sum + getCoordinateValue(s, "LONGITUDE", "longitude"),
          0,
        ) / schedulesWithCoordinates.value.length;

      return [avgLat, avgLng];
    });

    const mapZoom = computed(() => {
      return schedulesWithCoordinates.value.length > 1 ? 12 : 14;
    });

    // スケジュールを日付ごとにグループ化
    const groupedSchedules = computed(() => {
      // 旅行期間の全日付を取得
      const allDates = generateTripDates();

      // フィルター適用
      let filteredSchedules = scheduleStore.schedules;

      // メンバーフィルター
      if (selectedMemberFilter.value !== "all") {
        const selectedMemberCode = selectedMemberFilter.value;
        filteredSchedules = filteredSchedules.filter((schedule) => {
          return (
            schedule.MEMBERS &&
            schedule.MEMBERS.some(
              (member) => member.MEMBER_CODE === selectedMemberCode,
            )
          );
        });
      }

      // カテゴリフィルター
      if (selectedCategoryFilter.value !== "all") {
        filteredSchedules = filteredSchedules.filter(
          (schedule) => schedule.CATEGORY_CDV === selectedCategoryFilter.value,
        );
      }

      // 要不要フィルター
      if (selectedNeedFilter.value !== "all") {
        if (selectedNeedFilter.value === "needed") {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => schedule.NEED_FLG !== "0",
          );
        } else if (selectedNeedFilter.value === "not-needed") {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => schedule.NEED_FLG === "0",
          );
        }
      }

      // 予約状況フィルター
      if (selectedReservationFilter.value !== "all") {
        if (selectedReservationFilter.value === "need-reservation") {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => schedule.RESERVATION_FLG === "1",
          );
        } else if (selectedReservationFilter.value === "reservation-done") {
          filteredSchedules = filteredSchedules.filter(
            (schedule) =>
              schedule.RESERVATION_FLG === "1" &&
              schedule.RESERVATION_DONE_FLG === "1",
          );
        } else if (selectedReservationFilter.value === "reservation-pending") {
          filteredSchedules = filteredSchedules.filter(
            (schedule) =>
              schedule.RESERVATION_FLG === "1" &&
              schedule.RESERVATION_DONE_FLG !== "1",
          );
        } else if (selectedReservationFilter.value === "no-reservation") {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => schedule.RESERVATION_FLG !== "1",
          );
        }
      }

      // 費用フィルター
      if (selectedCostFilter.value !== "all") {
        if (selectedCostFilter.value === "with-cost") {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => schedule.COST && Number(schedule.COST) > 0,
          );
        } else if (selectedCostFilter.value === "no-cost") {
          filteredSchedules = filteredSchedules.filter(
            (schedule) => !schedule.COST || Number(schedule.COST) === 0,
          );
        }
      }

      const scheduleOccursOn = (schedule, dateKey) => {
        const start = toDateKey(schedule.START_YMD);
        const end = toDateKey(schedule.END_YMD || start);
        return Boolean(start && start <= dateKey && dateKey <= end);
      };

      const scheduleForDate = (schedule, dateKey) => {
        const start = toDateKey(schedule.START_YMD);
        const end = toDateKey(schedule.END_YMD || start);
        const isFirstDay = start === dateKey;
        const isLastDay = end === dateKey;
        const isMiddleDay = !isFirstDay && !isLastDay;
        return {
          ...schedule,
          DISPLAY_DATE: dateKey,
          DISPLAY_START_TIME: isFirstDay ? schedule.START_TIME : "00:00",
          DISPLAY_END_TIME: isLastDay ? schedule.END_TIME : "23:59",
          DISPLAY_COST: isFirstDay ? schedule.COST : null,
          IS_MULTI_DAY_CONTINUATION: !isFirstDay,
          IS_MULTI_DAY_MIDDLE: isMiddleDay,
        };
      };

      // 各日付に対してグループを作成
      const groups = allDates.map((dateKey, index) => {
        const daySchedules = filteredSchedules
          .filter((schedule) => scheduleOccursOn(schedule, dateKey))
          .map((schedule) => scheduleForDate(schedule, dateKey))
          .sort((a, b) => {
            // ①開始時刻でソート
            const timeA = a.DISPLAY_START_TIME || "00:00";
            const timeB = b.DISPLAY_START_TIME || "00:00";
            if (timeA !== timeB) {
              return timeA.localeCompare(timeB);
            }
            // ②開始時刻が同じ場合、終了時刻でソート
            const endTimeA = a.DISPLAY_END_TIME || "00:00";
            const endTimeB = b.DISPLAY_END_TIME || "00:00";
            return endTimeA.localeCompare(endTimeB);
          })
          .map((schedule, scheduleIndex) => ({
            ...schedule,
            MAP_PIN_NUMBER: scheduleIndex + 1,
          }));

        return {
          date: `${index + 1}日目 : ${formatDate(dateKey)}`,
          dateStr: formatDateShort(dateKey),
          dateObj: dateKey, // YYYY-MM-DD形式の日付文字列を追加
          items: daySchedules,
        };
      });

      // 初期状態で全て開く
      groups.forEach((_, index) => {
        if (openDays[index] === undefined) {
          openDays[index] = true;
        }
      });

      return groups;
    });

    const activeDayGroup = computed(
      () => groupedSchedules.value[activeDay.value] || null,
    );

    const activeDayItemsWithCoordinates = computed(() =>
      getItemsWithCoordinates(activeDayGroup.value?.items || [])
        .filter((item) => Boolean(item?.ADDRESS_NMV?.trim()))
        .map((item) => ({
          ...item,
          googleMapsUrl: getMapUrl(item),
        })),
    );

    const activeDayMapCenter = computed(() =>
      getMapCenter(activeDayItemsWithCoordinates.value),
    );

    const activeDayMapZoom = computed(() =>
      getMapZoom(activeDayItemsWithCoordinates.value),
    );

    const activeDayMapTitle = computed(() => {
      const dayNumber = activeDay.value + 1;
      const dateLabel = activeDayGroup.value?.dateStr || "";
      return dateLabel
        ? `${dayNumber}日目の訪問先マップ`
        : "訪問先マップ";
    });

    const getRouteQueryValue = (item) => {
      const lat = getCoordinateValue(item, "LATITUDE", "latitude");
      const lng = getCoordinateValue(item, "LONGITUDE", "longitude");
      if (lat !== null && lng !== null) return `${lat},${lng}`;
      return item.ADDRESS_NMV || item.SUBJECT_NMV || "";
    };

    const buildGoogleRouteUrl = (items) => {
      const points = items.map(getRouteQueryValue).filter(Boolean);
      if (points.length < 2) return null;
      const params = new URLSearchParams({
        api: "1",
        origin: points[0],
        destination: points[points.length - 1],
        travelmode: "transit",
      });
      const waypoints = points.slice(1, -1);
      if (waypoints.length > 0) {
        params.set("waypoints", waypoints.slice(0, 8).join("|"));
      }
      return `https://www.google.com/maps/dir/?${params.toString()}`;
    };

    const MOVE_MINUTES_PER_LEG = 25;

    const timeToMinutes = (time) => {
      const match = String(time || "").match(/^(\d{1,2}):(\d{2})/);
      if (!match) return null;
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (hours > 23 || minutes > 59) return null;
      return hours * 60 + minutes;
    };

    const buildRouteTimingChecks = (items) =>
      items.slice(0, -1).map((item, index) => {
        const nextItem = items[index + 1];
        const endMinutes = timeToMinutes(
          item.DISPLAY_END_TIME || item.END_TIME,
        );
        const nextStartMinutes = timeToMinutes(
          nextItem.DISPLAY_START_TIME || nextItem.START_TIME,
        );
        if (endMinutes === null || nextStartMinutes === null) {
          return {
            key: `${item.LINE_NON}-${nextItem.LINE_NON}`,
            needsAttention: false,
            unconfirmed: true,
            label: "移動時間を確認",
          };
        }
        const availableMinutes = nextStartMinutes - endMinutes;
        const marginMinutes = availableMinutes - MOVE_MINUTES_PER_LEG;
        const needsAttention = marginMinutes < 0;
        return {
          key: `${item.LINE_NON}-${nextItem.LINE_NON}`,
          needsAttention,
          label: needsAttention
            ? `移動要確認 ${Math.abs(marginMinutes)}分不足`
            : `移動余裕 +${marginMinutes}分`,
        };
      });

    const estimateMoveMinutes = (items) => {
      const visitCount = items.length;
      if (visitCount < 2) return 0;
      return (visitCount - 1) * MOVE_MINUTES_PER_LEG;
    };

    const activeDayRouteSummary = computed(() => {
      const items = activeDayGroup.value?.items || [];
      const routeItems = items.filter(
        (item) => getRouteQueryValue(item) && item.NEED_FLG !== "0",
      );
      const visitCount = routeItems.length;
      const moveMinutes = estimateMoveMinutes(routeItems);
      if (visitCount < 2) {
        return {
          title: "ルート候補がまだ少なめです",
          description:
            "場所付きの予定を2件以上追加すると、日別ルートと移動時間の目安を確認できます。",
          mapUrl: null,
          timingChecks: [],
        };
      }
      const legCount = Math.max(routeItems.length - 1, 0);
      const timingChecks = buildRouteTimingChecks(routeItems);
      const attentionCount = timingChecks.filter(
        (check) => check.needsAttention && !check.unconfirmed,
      ).length;
      const unconfirmedCount = timingChecks.filter(
        (check) => check.unconfirmed,
      ).length;
      let timingDescription = `${legCount}区間すべてに移動余裕があります。`;
      if (attentionCount && unconfirmedCount) {
        timingDescription = `${legCount}区間中${attentionCount}区間は移動要確認、${unconfirmedCount}区間は時刻未確認です。`;
      } else if (attentionCount) {
        timingDescription = `${legCount}区間中${attentionCount}区間は移動確認が必要です。`;
      } else if (unconfirmedCount) {
        timingDescription = `${legCount}区間中${unconfirmedCount}区間は時刻未確認です。`;
      }
      return {
        title: `${visitCount}スポット / ${legCount}区間 / 移動目安 ${moveMinutes}分`,
        description: timingChecks.length
          ? `${timingDescription}実際の交通手段や時刻表は外部地図で確認してください。`
          : "保存された順番でGoogle Mapsに渡します。実際の交通手段や時刻表は外部地図で確認してください。",
        mapUrl: buildGoogleRouteUrl(routeItems),
        timingChecks,
      };
    });

    const reservationItems = computed(() =>
      scheduleStore.schedules
        .filter((item) => item.RESERVATION_FLG === "1")
        .slice()
        .sort((a, b) => {
          const dateCompare = String(a.START_YMD || "").localeCompare(
            String(b.START_YMD || ""),
          );
          if (dateCompare !== 0) return dateCompare;
          return String(a.START_TIME || "").localeCompare(
            String(b.START_TIME || ""),
          );
        }),
    );

    const reservationSummary = computed(() => {
      const total = reservationItems.value.length;
      const done = reservationItems.value.filter(
        (item) => item.RESERVATION_DONE_FLG === "1",
      ).length;
      return {
        total,
        done,
        pending: total - done,
      };
    });

    const reservationWatchItems = computed(() =>
      reservationItems.value.slice(0, 5),
    );

    const currentLiveItem = computed(
      () =>
        getTripLiveState(
          scheduleStore.schedules,
          currentShiori.value,
          liveNow.value,
        ).currentItem,
    );

    const isCurrentLiveSchedule = (item) => {
      return Boolean(
        item?.LINE_NON && item.LINE_NON === currentLiveItem.value?.source.LINE_NON,
      );
    };

    const focusFirstDayWithCoordinates = () => {
      const currentDayHasCoordinates =
        activeDayItemsWithCoordinates.value.length > 0;
      if (currentDayHasCoordinates) return;

      const firstDayWithCoordinates = groupedSchedules.value.findIndex((day) =>
        day.items.some(hasCoordinates),
      );
      if (firstDayWithCoordinates >= 0) {
        activeDay.value = firstDayWithCoordinates;
      }
    };

    // 日のトグル
    const toggleDay = (index) => {
      openDays[index] = !openDays[index];
    };

    const getScheduleTimeLabel = (item) => {
      const start = item.DISPLAY_START_TIME || item.START_TIME || "";
      const end = item.DISPLAY_END_TIME || item.END_TIME || "";
      const displayStart = formatScheduleTimeLabel(start);
      const displayEnd = formatScheduleTimeLabel(end);
      if (displayStart && displayEnd) return `${displayStart} 〜 ${displayEnd}`;
      return displayStart || displayEnd;
    };

    const getScheduleEndTimeLabel = (item) => {
      return formatScheduleTimeLabel(item.DISPLAY_END_TIME || item.END_TIME || "");
    };

    const formatScheduleTimeLabel = (time) => {
      return time === "00:00" ? "0:00" : time;
    };

    // スケジュールにスクロールするヘルパー関数
    const scrollToSchedule = async (scheduleLineNon, scheduleStartYmd) => {
      if (!scheduleLineNon || !scheduleStartYmd) {
        console.log("[ScheduleView] スクロール対象のデータが不足:", {
          scheduleLineNon,
          scheduleStartYmd,
        });
        return;
      }

      console.log("[ScheduleView] スクロール処理開始:", {
        scheduleLineNon,
        scheduleStartYmd,
      });

      // スケジュールリストが更新されるまで短時間待機
      await nextTick();

      // 該当する日のタブインデックスを見つける
      const allDates = generateTripDates();
      const dayIndex = allDates.findIndex((date) => date === scheduleStartYmd);

      console.log(
        "[ScheduleView] タブインデックス:",
        dayIndex,
        "全日付:",
        allDates,
      );

      if (dayIndex !== -1) {
        // タブを切り替える
        activeDay.value = dayIndex;
        console.log("[ScheduleView] タブ切り替え完了:", dayIndex);

        // DOMが更新されるのを待つ
        await nextTick();

        // 少し待機してからスケジュール要素を探す
        setTimeout(() => {
          const scheduleElement = Array.from(
            document.querySelectorAll("[data-schedule-id]"),
          ).find(
            (element) =>
              element.dataset.scheduleId === String(scheduleLineNon) &&
              element.offsetParent !== null,
          );

          console.log("[ScheduleView] スケジュール要素:", scheduleElement);

          if (scheduleElement) {
            scheduleElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });

            if (window.matchMedia("(max-width: 640px)").matches) {
              const rect = scheduleElement.getBoundingClientRect();
              const stickyOffset = 124;
              window.scrollTo({
                top: window.scrollY + rect.top - stickyOffset,
                behavior: "smooth",
              });
            }

            // ハイライト効果を追加
            scheduleElement.style.transition = "background-color 0.5s";
            scheduleElement.style.backgroundColor =
              "var(--primary-light, #dbeafe)";
            setTimeout(() => {
              scheduleElement.style.backgroundColor = "";
            }, 2000);
          } else {
            console.warn("[ScheduleView] スケジュール要素が見つかりません");
          }
        }, 300);
      } else {
        console.warn("[ScheduleView] 該当する日付が見つかりません");
      }
    };

    const focusScheduleFromQuery = async (scheduleLineNon) => {
      if (!scheduleLineNon) return;
      const target = scheduleStore.schedules.find(
        (item) => String(item.LINE_NON) === String(scheduleLineNon),
      );
      if (target) {
        await scrollToSchedule(target.LINE_NON, target.START_YMD);
      }
    };

    // スケジュール作成完了時の処理
    const onScheduleCreated = async (result) => {
      console.log("スケジュールが作成されました:", result);
      editMode.value = false;
      editingSchedule.value = null;
      prefillScheduleData.value = null;

      // 作成されたスケジュールにスクロール（dataオブジェクトから取得）
      if (result?.data?.LINE_NON && result?.data?.START_YMD) {
        await scrollToSchedule(result.data.LINE_NON, result.data.START_YMD);
      }
    };

    // スケジュール更新完了時の処理
    const onScheduleUpdated = async (result) => {
      console.log("スケジュールが更新されました:", result);
      const scrollY = scrollYBeforeEdit.value || window.scrollY;
      const scrollX = window.scrollX;
      editMode.value = false;
      editingSchedule.value = null;
      prefillScheduleData.value = null;

      if (shioriId.value) {
        await scheduleStore.fetchSchedules(shioriId.value, { silent: true });
      }

      await nextTick();
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: scrollX, behavior: "auto" });
        scrollYBeforeEdit.value = 0;
      });
    };

    // スケジュール新規作成モードで開く
    const openCreateModal = () => {
      if (!props.canWrite) return;
      editMode.value = false;
      editingSchedule.value = null;
      prefillScheduleData.value = null;
      // 現在表示中の日程タブの日付をデフォルトに設定
      initialDate.value = groupedSchedules.value[activeDay.value]?.dateObj || null;
      showModal.value = true;
    };

    // 特定の日付でスケジュール作成モードを開く
    const openCreateModalForDate = (dateString) => {
      if (!props.canWrite) return;
      editMode.value = false;
      editingSchedule.value = null;
      prefillScheduleData.value = null;
      initialDate.value = dateString; // YYYY-MM-DD形式の日付を設定
      showModal.value = true;
    };

    const openTemplateModal = (template) => {
      if (!props.canWrite) return;
      editMode.value = false;
      editingSchedule.value = null;
      initialDate.value = groupedSchedules.value[activeDay.value]?.dateObj || null;
      prefillScheduleData.value = {
        title: template.title,
        startTime: template.startTime,
        endTime: template.endTime,
        category: template.category,
        transportation: template.transportation || "",
        remarks: template.remarks,
        reservation: template.reservation,
        reservationDone: false,
        needFlg: true,
      };
      showModal.value = true;
    };

    const startInlineEdit = (item) => {
      if (!props.canWrite) return;
      if (inlineEdit.lineNon === item.LINE_NON) return;
      inlineEdit.lineNon = item.LINE_NON;
      inlineEdit.title = item.SUBJECT_NMV || "";
      inlineEdit.startTime = item.START_TIME || "";
      inlineEdit.endTime = item.END_TIME || "";
      inlineEdit.endDate = item.END_YMD || item.START_YMD;
      inlineEdit.originalTitle = item.SUBJECT_NMV || "";
      inlineEdit.originalStartTime = item.START_TIME || "";
      inlineEdit.originalEndTime = item.END_TIME || "";
      inlineEdit.originalEndDate = item.END_YMD || item.START_YMD;
      inlineEdit.error = "";
    };

    const cancelInlineEdit = (lineNon = null) => {
      if (lineNon && inlineEdit.lineNon !== lineNon) return;
      inlineEdit.lineNon = null;
      inlineEdit.title = "";
      inlineEdit.startTime = "";
      inlineEdit.endTime = "";
      inlineEdit.endDate = "";
      inlineEdit.originalTitle = "";
      inlineEdit.originalStartTime = "";
      inlineEdit.originalEndTime = "";
      inlineEdit.originalEndDate = "";
      inlineEdit.error = "";
    };

    const toDateTime = (dateValue, timeValue) => {
      if (!dateValue || !timeValue) return null;
      const [year, month, day] = dateValue.split("-").map(Number);
      const [hour, minute] = timeValue.split(":").map(Number);
      return new Date(year, month - 1, day, hour, minute, 0, 0);
    };

    const formatLocalDate = (dateValue) =>
      [
        dateValue.getFullYear(),
        String(dateValue.getMonth() + 1).padStart(2, "0"),
        String(dateValue.getDate()).padStart(2, "0"),
      ].join("-");

    const formatLocalTime = (dateValue) =>
      [
        String(dateValue.getHours()).padStart(2, "0"),
        String(dateValue.getMinutes()).padStart(2, "0"),
      ].join(":");

    const updateInlineStartTime = (item, startTime) => {
      if (!props.canWrite) return;
      if (inlineEdit.lineNon !== item.LINE_NON) startInlineEdit(item);
      inlineEdit.startTime = startTime;
      inlineEdit.error = "";

      const originalStart = toDateTime(
        item.START_YMD,
        inlineEdit.originalStartTime,
      );
      const originalEnd = toDateTime(
        inlineEdit.originalEndDate,
        inlineEdit.originalEndTime,
      );
      const nextStart = toDateTime(item.START_YMD, startTime);
      if (!originalStart || !originalEnd || !nextStart) return;

      const duration = originalEnd.getTime() - originalStart.getTime();
      const nextEnd = new Date(nextStart.getTime() + duration);
      inlineEdit.endDate = formatLocalDate(nextEnd);
      inlineEdit.endTime = formatLocalTime(nextEnd);
    };

    const updateInlineTitle = (item, title) => {
      if (!props.canWrite) return;
      if (inlineEdit.lineNon !== item.LINE_NON) startInlineEdit(item);
      inlineEdit.title = title;
      inlineEdit.error = "";
    };

    const handleInlineBlur = (event, item) => {
      if (!props.canWrite) return;
      const nextInputId = event.relatedTarget?.dataset?.inlineEditId;
      if (nextInputId === item.LINE_NON) return;
      saveInlineEdit(item);
    };

    const saveInlineEdit = async (item) => {
      if (!props.canWrite) return;
      if (
        inlineEdit.lineNon !== item.LINE_NON ||
        inlineEdit.savingLineNon === item.LINE_NON
      ) {
        return;
      }
      inlineEdit.error = "";
      if (!inlineEdit.title.trim()) {
        inlineEdit.error = "予定名を入力してください。";
        return;
      }
      if (!inlineEdit.startTime) {
        inlineEdit.error = "開始時刻を入力してください。";
        return;
      }
      const editSnapshot = {
        lineNon: item.LINE_NON,
        title: inlineEdit.title.trim(),
        startTime: inlineEdit.startTime,
        endTime: inlineEdit.endTime,
        endDate: inlineEdit.endDate || item.END_YMD || item.START_YMD,
        originalTitle: inlineEdit.originalTitle,
        originalStartTime: inlineEdit.originalStartTime,
      };
      if (
        editSnapshot.title === editSnapshot.originalTitle &&
        editSnapshot.startTime === editSnapshot.originalStartTime
      ) {
        cancelInlineEdit(item.LINE_NON);
        return;
      }
      const members = (item.MEMBERS || []).map((member) => ({
        member_code: member.MEMBER_CODE,
        split_participate_flg: member.SPLIT_PARTICIPATE_FLG || "1",
      }));
      const paidByMember = (item.MEMBERS || []).find(
        (member) =>
          Number(member.PAID_AMOUNT || 0) > 0 &&
          Number(member.PAID_AMOUNT) === Number(item.COST || 0),
      );

      inlineEdit.savingLineNon = item.LINE_NON;
      try {
        await scheduleStore.updateSchedule(
          shioriId.value,
          item.LINE_NON,
          {
            shioriId: shioriId.value,
            title: editSnapshot.title,
            startDate: item.START_YMD,
            startTime: editSnapshot.startTime,
            endDate: editSnapshot.endDate,
            endTime: editSnapshot.endTime || null,
            timeDifference: String(item.TIME_DIFFERENCE_NUM || 9),
            googlePlaceNmv:
              item.GOOGLE_PLACE_NMV || item.googlePlaceNmv || null,
            googlePlaceId: item.GOOGLE_PLACE_ID || item.googlePlaceId || null,
            category: item.CATEGORY_CDV || null,
            url: item.URL_NMV || null,
            transportation: item.TRANSPORTATION_NMV || null,
            tel: [
              item.TEL_AREA_CODE,
              item.TEL_CITY_CODE,
              item.TEL_SUBSCRIBER_NUMBER,
            ]
              .filter(Boolean)
              .join("-"),
            address: item.ADDRESS_NMV || null,
            latitude: item.LATITUDE || null,
            longitude: item.LONGITUDE || null,
            remarks: item.REMARKS || null,
            cost: item.COST || null,
            currency: item.CURRENCY || "JPY",
            reservation: item.RESERVATION_FLG === "1",
            needFlg: item.NEED_FLG !== "0",
            reservationDone: item.RESERVATION_DONE_FLG === "1",
            reservationDocumentName: item.RESERVATION_DOCUMENT_NAME || null,
            reservationDocumentUrl: item.RESERVATION_DOCUMENT_URL || null,
            paidBy: paidByMember?.MEMBER_CODE || null,
            members,
          },
          { refresh: false, silent: true },
        );
        const updatedValues = {
          SUBJECT_NMV: editSnapshot.title,
          START_TIME: editSnapshot.startTime,
          END_YMD: editSnapshot.endDate,
          END_TIME: editSnapshot.endTime || null,
          DISPLAY_START_TIME: editSnapshot.startTime,
        };
        Object.assign(item, updatedValues);
        const storedItem = scheduleStore.schedules.find(
          (schedule) => schedule.LINE_NON === item.LINE_NON,
        );
        if (storedItem && storedItem !== item) {
          Object.assign(storedItem, updatedValues);
        }
        cancelInlineEdit(editSnapshot.lineNon);
      } catch (error) {
        if (inlineEdit.lineNon === editSnapshot.lineNon) {
          inlineEdit.error =
            error.response?.data?.detail ||
            "開始時刻と予定名を更新できませんでした。";
        }
      } finally {
        if (inlineEdit.savingLineNon === editSnapshot.lineNon) {
          inlineEdit.savingLineNon = null;
        }
      }
    };

    // スケジュール編集
    const editSchedule = (schedule) => {
      if (!props.canWrite) return;
      const sourceSchedule =
        scheduleStore.schedules.find(
          (item) => item.LINE_NON === schedule.LINE_NON,
        ) || schedule;
      scrollYBeforeEdit.value = window.scrollY;
      editMode.value = true;
      prefillScheduleData.value = null;
      editingSchedule.value = { ...sourceSchedule };
      showModal.value = true;
    };

    const focusScheduleOnMap = async (schedule) => {
      if (!hasCoordinates(schedule)) return;
      focusedScheduleMapItemKey.value = null;
      await nextTick();
      focusedScheduleMapItemKey.value = schedule.LINE_NON;
    };

    const handleScheduleItemClick = (event, schedule) => {
      const interactiveSelector =
        "a, button, input, select, textarea, [role='button'], .item-actions, .mobile-action-menu";
      if (event.target.closest(interactiveSelector)) return;
      focusScheduleOnMap(schedule);
      if (!window.matchMedia("(max-width: 640px)").matches) return;
      if (!props.canWrite) return;
      editSchedule(schedule);
    };

    // スケジュールコピー
    const copySchedule = (schedule) => {
      if (!props.canWrite) return;
      const sourceSchedule =
        scheduleStore.schedules.find(
          (item) => item.LINE_NON === schedule.LINE_NON,
        ) || schedule;
      // 編集モードをオフにして、既存のスケジュールデータをコピー
      editMode.value = false;
      prefillScheduleData.value = null;
      editingSchedule.value = null;

      // コピー元のデータを新規作成用に整形
      // LINE_NONやその他のIDは除外し、内容のみコピー
      const copiedData = {
        SUBJECT_NMV: `${sourceSchedule.SUBJECT_NMV}（コピー）`,
        START_YMD: sourceSchedule.START_YMD,
        START_TIME: sourceSchedule.START_TIME,
        END_YMD: sourceSchedule.END_YMD,
        END_TIME: sourceSchedule.END_TIME,
        CATEGORY_CDV: sourceSchedule.CATEGORY_CDV,
        URL_NMV: sourceSchedule.URL_NMV,
        TRANSPORTATION_NMV: sourceSchedule.TRANSPORTATION_NMV,
        TEL_AREA_CODE: sourceSchedule.TEL_AREA_CODE,
        TEL_CITY_CODE: sourceSchedule.TEL_CITY_CODE,
        TEL_SUBSCRIBER_NUMBER: sourceSchedule.TEL_SUBSCRIBER_NUMBER,
        ZIP_CODE1: sourceSchedule.ZIP_CODE1,
        ZIP_CODE2: sourceSchedule.ZIP_CODE2,
        PREFECTURE: sourceSchedule.PREFECTURE,
        CITY: sourceSchedule.CITY,
        TOWN: sourceSchedule.TOWN,
        ADDRESS_NMV: sourceSchedule.ADDRESS_NMV,
        GOOGLE_PLACE_NMV:
          sourceSchedule.GOOGLE_PLACE_NMV || sourceSchedule.googlePlaceNmv || null,
        GOOGLE_PLACE_ID:
          sourceSchedule.GOOGLE_PLACE_ID || sourceSchedule.googlePlaceId || null,
        BUILDING: sourceSchedule.BUILDING,
        LATITUDE: sourceSchedule.LATITUDE,
        LONGITUDE: sourceSchedule.LONGITUDE,
        REMARKS: sourceSchedule.REMARKS,
        COST: sourceSchedule.COST,
        CURRENCY: sourceSchedule.CURRENCY,
        RESERVATION_FLG: sourceSchedule.RESERVATION_FLG,
        NEED_FLG: sourceSchedule.NEED_FLG,
        RESERVATION_DONE_FLG: sourceSchedule.RESERVATION_DONE_FLG,
        RESERVATION_DOCUMENT_NAME: sourceSchedule.RESERVATION_DOCUMENT_NAME,
        RESERVATION_DOCUMENT_URL: sourceSchedule.RESERVATION_DOCUMENT_URL,
        MEMBERS: sourceSchedule.MEMBERS,
      };

      prefillScheduleData.value = {
        title: copiedData.SUBJECT_NMV,
        startDate: copiedData.START_YMD,
        startTime: copiedData.START_TIME,
        endDate: copiedData.END_YMD,
        endTime: copiedData.END_TIME,
        category: copiedData.CATEGORY_CDV,
        url: copiedData.URL_NMV,
        transportation: copiedData.TRANSPORTATION_NMV,
        address: [
          copiedData.PREFECTURE,
          copiedData.CITY,
          copiedData.TOWN,
          copiedData.ADDRESS_NMV,
          copiedData.BUILDING,
        ]
          .filter(Boolean)
          .join(" "),
        googlePlaceNmv: copiedData.GOOGLE_PLACE_NMV,
        googlePlaceId: copiedData.GOOGLE_PLACE_ID,
        latitude: copiedData.LATITUDE,
        longitude: copiedData.LONGITUDE,
        remarks: copiedData.REMARKS,
        cost: copiedData.COST,
        currency: copiedData.CURRENCY,
        reservation: copiedData.RESERVATION_FLG === "1",
        needFlg: copiedData.NEED_FLG !== "0",
        reservationDone: copiedData.RESERVATION_DONE_FLG === "1",
        reservationDocumentName: copiedData.RESERVATION_DOCUMENT_NAME,
        reservationDocumentUrl: copiedData.RESERVATION_DOCUMENT_URL,
      };
      showModal.value = true;
    };

    // スケジュール削除
    const deleteSchedule = async (lineNon) => {
      if (!props.canWrite) return;
      if (!confirm("このスケジュールを削除しますか？")) return;

      try {
        await scheduleStore.deleteSchedule(shioriId.value, lineNon);
        alert("スケジュールを削除しました");
      } catch (err) {
        console.error("スケジュール削除エラー:", err);
        alert("スケジュールの削除に失敗しました");
      }
    };

    // スケジュールからチェックリスト作成
    const createChecklistFromSchedule = async (schedule) => {
      if (!props.canWrite) return;
      try {
        const scheduleData = {
          shioriId: shioriId.value,
          scheduleLineNon: schedule.LINE_NON,
          itemName: `${schedule.SUBJECT_NMV}の準備`,
          categoryName: "スケジュール準備",
        };

        await checklistStore.createFromSchedule(scheduleData);
        alert("チェックリストを作成しました！");
      } catch (error) {
        console.error("チェックリスト作成エラー:", error);
        alert("チェックリストの作成に失敗しました");
      }
    };

    // 初回読み込み時にスケジュール、メンバー、しおり情報を取得
    const syncMobileViewport = (event) => {
      isMobileViewport.value = event.matches;
    };

    const selectMobilePanelAndFocus = async (panel) => {
      activeMobilePanel.value = panel;
      await nextTick();
      (panel === "list" ? mobileListTab.value : mobileMapTab.value)?.focus();
    };

    const handleMobileTabKeydown = (event) => {
      if (event.key === "Home") {
        event.preventDefault();
        selectMobilePanelAndFocus("list");
      } else if (event.key === "End") {
        event.preventDefault();
        selectMobilePanelAndFocus("map");
      } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        selectMobilePanelAndFocus(
          activeMobilePanel.value === "list" ? "map" : "list",
        );
      }
    };

    onMounted(async () => {
      mobileViewportQuery = window.matchMedia("(max-width: 640px)");
      isMobileViewport.value = mobileViewportQuery.matches;
      mobileViewportQuery.addEventListener("change", syncMobileViewport);
      liveTimerId = window.setInterval(() => {
        liveNow.value = new Date();
      }, 60000);

      // スケジュールカテゴリをロード（キャッシュ済みなら即返る）
      scheduleCategoryStore.fetchCategories();

      if (shioriId.value) {
        const fetches = [];
        // すでにデータがある場合はスキップして重複リクエストを防止
        if (scheduleStore.schedules.length === 0) {
          fetches.push(scheduleStore.fetchSchedules(shioriId.value));
        }
        if (!shioriMemberStore.members[shioriId.value]?.length) {
          fetches.push(shioriMemberStore.fetchMembers(shioriId.value));
        }
        if (shioriStore.shiori.length === 0) {
          fetches.push(shioriStore.fetchShiori());
        }
        if (fetches.length > 0) {
          await Promise.all(fetches);
        }

        // 共有しおりの場合: ownリストにない場合はsharedShioriを取得
        if (
          !shioriStore.shiori.find((s) => s.SHIORI_NOV === shioriId.value) &&
          shioriStore.sharedShiori.length === 0
        ) {
          await shioriStore.fetchSharedWithMe();
        }

        // デバッグ: スケジュールデータを確認
        console.log(
          "[ScheduleView] スケジュールデータ:",
          scheduleStore.schedules,
        );
        if (scheduleStore.schedules.length > 0) {
          console.log(
            "[ScheduleView] 最初のスケジュールのメンバー:",
            scheduleStore.schedules[0].MEMBERS,
          );
        }

        // ログインユーザー情報が取得できている場合、フィルターを設定
        if (authStore.user?.APP_USER_CODE) {
          const userCode = authStore.user.APP_USER_CODE;
          // このしおりのメンバーに含まれているか確認
          const isMember = shioriMembers.value.some(
            (member) => member.MEMBER_CODE === userCode,
          );
          // メンバーに含まれている場合のみフィルター設定
          if (isMember) {
            selectedMemberFilter.value = userCode;
          }
        }

        await focusScheduleFromQuery(route.query.schedule);
        focusFirstDayWithCoordinates();
      }
    });

    onUnmounted(() => {
      mobileViewportQuery?.removeEventListener("change", syncMobileViewport);
      if (liveTimerId) {
        window.clearInterval(liveTimerId);
      }
    });

    watch(
      groupedSchedules,
      () => {
        focusFirstDayWithCoordinates();
      },
      { flush: "post" },
    );

    watch(
      () => route.query.schedule,
      (scheduleLineNon) => {
        focusScheduleFromQuery(scheduleLineNon);
      },
    );

    return {
      showModal,
      shioriId,
      scheduleStore,
      currentShiori,
      groupedSchedules,
      schedulesWithCoordinates,
      hasCoordinates,
      getMapCenter,
      getMapZoom,
      activeDayItemsWithCoordinates,
      activeDayMapCenter,
      activeDayMapZoom,
      activeDayMapTitle,
      focusedScheduleMapItemKey,
      mapCenter,
      mapZoom,
      openDays,
      editMode,
      editingSchedule,
      prefillScheduleData,
      initialDate,
      scheduleTemplates,
      activeDayRouteSummary,
      getScheduleEndTimeLabel,
      reservationSummary,
      reservationWatchItems,
      selectedMemberFilter,
      selectedCategoryFilter,
      selectedNeedFilter,
      selectedReservationFilter,
      selectedCostFilter,
      hasActiveFilters,
      shioriMembers,
      isInternationalTrip,
      activeDay,
      activeMobilePanel,
      mobileListTab,
      mobileMapTab,
      handleMobileTabKeydown,
      isMobileViewport,
      totalCost,
      weatherLocation,
      uniqueLocations,
      filterSidebarOpen,
      openKebabItemId,
      inlineEdit,
      itineraryPasteText,
      itineraryDrafts,
      itinerarySkippedRows,
      parseItineraryText,
      openDraftModal,
      removeItineraryDraft,
      toggleDay,
      openCreateModal,
      openCreateModalForDate,
      openTemplateModal,
      startInlineEdit,
      updateInlineStartTime,
      updateInlineTitle,
      handleInlineBlur,
      onScheduleCreated,
      onScheduleUpdated,
      editSchedule,
      handleScheduleItemClick,
      copySchedule,
      deleteSchedule,
      createChecklistFromSchedule,
      getCategoryIcon,
      formatCost,
      formatCurrencyUnit,
      getCostParticipantCount,
      formatDate,
      formatDateShort,
      formatDateRange,
      getMapUrl,
      getReservationUrl,
      isRakutenTravelEligible,
      getReservationProviders,
      getScheduleTimeLabel,
      isCurrentLiveSchedule,
      clearFilters,
      scheduleCategoryStore,
      scheduleCategoryOptions,
    };
  },
};
</script>

<style scoped>
.schedule-view {
  padding: 2rem 1rem;
  --schedule-panel-height: calc(100vh - 1rem);
}

.schedule-page-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 1480px;
  margin: 0 auto;
}

.schedule-left-pane,
.schedule-map-pane {
  min-width: 0;
}

.mobile-view-tabs {
  display: none;
}

.schedule-container {
  max-width: 900px;
  margin: 0 auto;
}

.schedule-map-panel {
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

.schedule-map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-background);
}

.schedule-map-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  margin: 0;
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 700;
}

.schedule-map-count {
  flex-shrink: 0;
  padding: 0.25rem 0.625rem;
  border-radius: var(--radius-full);
  background: var(--primary-color);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
}

.schedule-map-body {
  position: relative;
  min-height: 360px;
}

.schedule-map-body :deep(.map-container) {
  min-height: 360px;
}

.schedule-map-body :deep(.map) {
  min-height: 360px;
}

.schedule-map-empty {
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  margin: 0;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.5;
}

.planning-assist-panel {
  display: grid;
  gap: 1rem;
  width: 100%;
  max-width: 1480px;
  margin: 1.5rem auto 1rem;
}

.assist-section {
  padding: 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.assist-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.875rem;
}

.assist-kicker {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--primary-hover);
  font-size: 0.72rem;
  font-weight: 800;
}

.assist-heading h2,
.route-summary-panel h3 {
  margin: 0;
  color: var(--color-text);
  font-size: 1rem;
  line-height: 1.35;
}

.assist-count {
  flex-shrink: 0;
  padding: 0.25rem 0.6rem;
  color: var(--primary-hover);
  background: var(--color-primary-light);
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 800;
}

.assist-count.warning {
  color: #c2410c;
  background: #fff7ed;
}

.template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.625rem;
}

.template-card {
  display: grid;
  gap: 0.35rem;
  justify-items: start;
  min-height: 86px;
  padding: 0.75rem;
  color: var(--color-text);
  text-align: left;
  background: var(--color-background);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  font: inherit;
}

.template-card:hover {
  border-color: var(--primary-color);
  box-shadow: var(--shadow-sm);
}

.template-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: var(--primary-hover);
  background: var(--color-surface);
  border-radius: var(--radius-md);
}

.template-card strong {
  font-size: 0.875rem;
  line-height: 1.35;
}

.template-card span:last-child {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.paste-import-section {
  display: grid;
  gap: 0.875rem;
}

.paste-import-body {
  display: grid;
  gap: 0.625rem;
}

.paste-import-label {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 800;
}

.paste-import-textarea {
  width: 100%;
  box-sizing: border-box;
  min-height: 112px;
  padding: 0.75rem 0.875rem;
  color: var(--color-text);
  background: linear-gradient(180deg, #ffffff 0%, #fbfdf8 100%);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font: inherit;
  line-height: 1.55;
  resize: vertical;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}

.paste-import-textarea:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(180, 207, 158, 0.18);
  outline: none;
}

.paste-import-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
  align-items: center;
}

.paste-parse-btn {
  min-height: 36px;
}

.paste-skip-count {
  padding: 0.25rem 0.625rem;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: var(--radius-md);
  font-size: 0.75rem;
  font-weight: 800;
}

.itinerary-draft-list {
  display: grid;
  gap: 0.625rem;
}

.itinerary-draft-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem;
  background: #f8fafc;
  border: 1px solid #dbeafe;
  border-radius: var(--radius-md);
}

.draft-card-main {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.625rem;
  align-items: center;
  min-width: 0;
}

.draft-card-main strong {
  min-width: 0;
  color: var(--color-text);
  font-size: 0.9rem;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.draft-date,
.draft-time,
.draft-link {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0.2rem 0.5rem;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1.2;
}

.draft-date {
  color: #047857;
  background: #ecfdf5;
}

.draft-time {
  color: #1d4ed8;
  background: #eff6ff;
}

.draft-link {
  color: #334155;
  background: var(--color-surface);
  border: 1px solid #cbd5e1;
  text-decoration: none;
}

.draft-card-actions {
  display: flex;
  gap: 0.4rem;
  align-items: center;
}

.draft-action-btn,
.draft-icon-btn {
  min-height: 34px;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 800;
}

.draft-action-btn {
  padding: 0.35rem 0.7rem;
}

.draft-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  color: var(--color-danger);
}

.draft-action-btn:hover,
.draft-icon-btn:hover {
  border-color: var(--primary-color);
  box-shadow: var(--shadow-sm);
}

.itinerary-skipped-list {
  display: grid;
  gap: 0.4rem;
  padding: 0.75rem;
  color: #7c2d12;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: var(--radius-md);
  font-size: 0.8125rem;
}

.itinerary-skipped-list ul {
  display: grid;
  gap: 0.35rem;
  margin: 0;
  padding-left: 1rem;
}

.itinerary-skipped-list span {
  display: block;
  color: #9a3412;
  font-size: 0.75rem;
}

.reservation-list {
  display: grid;
  gap: 0.5rem;
}

.reservation-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.625rem;
  text-align: left;
  background: var(--color-background);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  font: inherit;
}

.reservation-row:hover {
  border-color: var(--primary-color);
}

.reservation-row-date {
  color: var(--primary-hover);
  font-size: 0.75rem;
  font-weight: 800;
}

.reservation-row-main {
  display: grid;
  min-width: 0;
  gap: 0.15rem;
}

.reservation-row-main strong {
  overflow: hidden;
  color: var(--color-text);
  font-size: 0.85rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reservation-row-main small {
  color: var(--color-text-muted);
  font-size: 0.72rem;
}

.reservation-row-link,
.route-timing-check.unconfirmed {
  border-color: #b8c1cc;
  background: #f1f4f7;
  color: #475569;
}

.route-summary-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0.35rem 0.7rem;
  color: white;
  background: var(--primary-hover);
  border-radius: var(--radius-md);
  font-size: 0.75rem;
  font-weight: 800;
  text-decoration: none;
}

.assist-empty {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.route-summary-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--color-border-light);
  background: #fbfcfa;
}

.route-summary-panel p {
  margin: 0.25rem 0 0;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.route-summary-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}

.route-summary-link {
  flex-shrink: 0;
  background: #1d4ed8;
}



/* 旅行概要セクション */
.trip-overview {
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  border-radius: var(--radius-xl);
  padding: 1.75rem;
  margin-bottom: 2rem;
  color: white;
  box-shadow: var(--shadow-lg);
}

.overview-header {
  margin-bottom: 1.25rem;
}

.overview-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.overview-content {
  display: flex;
  flex-direction: column; /* mobile */
  gap: 1rem; /* mobile */
}

.overview-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 500;
}

.overview-icon {
  opacity: 0.9;
}

/* アクションバー */
.action-bar {
  margin-bottom: 1.5rem;
  position: relative;
}

/* モバイル: フィルタートグルボタン */
.filter-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
  cursor: pointer;
  position: relative;
  box-shadow: var(--shadow-sm);
}

.filter-active-dot {
  width: 8px;
  height: 8px;
  background: var(--primary-color);
  border-radius: 50%;
}

/* フィルターオーバーレイ（モバイル） */
.filter-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 5000;
}

/* フィルターサイドバー */
.filters-sidebar {
  position: fixed;
  top: 0;
  left: -100%;
  width: min(320px, 85vw);
  height: 100dvh;
  background: var(--color-surface);
  z-index: 5001;
  transition: left 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: var(--shadow-xl);
  overflow-y: auto;
}

.filters-sidebar.is-open {
  left: 0;
}

.filters-sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--color-border-light);
  font-weight: 700;
  font-size: 1rem;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
  flex-shrink: 0;
}

.sidebar-close-btn {
  width: 2rem;
  height: 2rem;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: var(--radius-md);
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filters-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  flex: 1;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  width: 100%;
}

.filter-label {
  font-size: 0.8125rem;
  color: var(--color-text-muted);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.filter-select {
  padding: 0.5rem 0.875rem;
  font-size: 0.9rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  transition: all var(--transition-base);
}

.filter-select:hover {
  border-color: var(--primary-color);
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.clear-filters-btn {
  padding: 0.5rem 1rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-base);
  display: flex;
  align-items: center;
  gap: 0.375rem;
  align-self: flex-end;
  width: 100%; /* mobile */
  justify-content: center; /* mobile */
}

.clear-filters-btn:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

/* 地図セクション */
.map-section {
  margin-bottom: 2rem;
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  padding: 1.5rem;
  box-shadow: var(--shadow-md);
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.visited-locations {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.location-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: var(--primary-lightest);
  color: var(--primary-color);
  border-radius: var(--radius-full);
  font-size: 0.8125rem;
  font-weight: 500;
}

.day-map-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  padding: 1rem;
  box-shadow: var(--shadow-sm);
  min-width: 0;
}

.day-map-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
  color: var(--color-text);
  font-size: 0.95rem;
  font-weight: 700;
}

.day-map-section :deep(.map) {
  min-height: 280px;
}

.schedule-day-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* タブスタイル */
.schedule-tabs {
  background: var(--color-surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  overflow: visible;
}

.tabs-header {
  display: flex;
  position: relative;
  z-index: 80;
  border-bottom: 2px solid var(--color-border-light);
  background: var(--color-background);
  overflow-x: auto;
  scrollbar-width: thin;
}

.tabs-header::-webkit-scrollbar {
  height: 4px;
}

.tabs-header::-webkit-scrollbar-track {
  background: var(--color-cool-bg);
}

.tabs-header::-webkit-scrollbar-thumb {
  background: var(--primary-color);
  border-radius: var(--radius-sm);
}

.tab-button {
  flex: 1 1 auto; /* mobile: stretch */
  min-width: 100px; /* mobile */
  padding: 1rem 1.5rem;
  background: transparent;
  border: none;
  color: var(--color-text-muted);
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  position: relative;
  transition: all var(--transition-base);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-button:hover {
  color: var(--primary-color);
  background: var(--primary-lightest);
}

.tab-button.active {
  color: var(--primary-color);
  background: var(--color-surface);
}

.tab-button.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--primary-color);
}

.tab-count {
  background: var(--primary-color);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.125rem 0.5rem;
  border-radius: var(--radius-full);
  min-width: 20px;
  text-align: center;
}

/* フローティングボタン */
.floating-btn {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-hover) 100%
  );
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(180, 207, 158, 0.4),
    0 8px 24px rgba(180, 207, 158, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  z-index: 4000;
}

.floating-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(180, 207, 158, 0.5),
    0 12px 32px rgba(180, 207, 158, 0.3);
}

.floating-btn:active {
  transform: scale(1.05);
}

.floating-btn-icon {
  font-size: 1.25rem;
  line-height: 1;
  color: #000;
}

.floating-btn-icon img {
  filter: brightness(0);
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.filter-label {
  font-size: 0.9rem;
  color: var(--color-text);
  font-weight: 500;
}

.filter-select {
  padding: 0.5rem 0.875rem;
  font-size: 0.9rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  transition: all var(--transition-base);
}

.filter-select:hover {
  border-color: var(--primary-color);
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-icon {
  font-size: 1rem;
}

/* ローディング・空状態 */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  text-align: center;
}

.empty-icon {
  font-size: 4rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
}

.empty-description {
  font-size: 0.9375rem;
  color: var(--color-text-muted);
  margin: 0;
}

/* スケジュールリスト */
.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.day-group {
  width: 100%;
}

.day-card {
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  overflow: visible;
  transition: all var(--transition-base);
}

.day-card:hover {
  box-shadow: var(--shadow-lg);
}

.day-header {
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  color: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all var(--transition-fast);
}

.day-header:hover {
  background: linear-gradient(135deg, #5568d3 0%, #6a3f8e 100%);
}

.day-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.day-title {
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.day-header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.add-schedule-btn {
  padding: 0.375rem 0.75rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: var(--radius-md);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.add-schedule-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.05);
}

.add-schedule-btn:active {
  transform: scale(0.98);
}

.day-toggle {
  font-size: 1.25rem;
  transition: transform var(--transition-fast);
}

.day-content {
  padding: 1.5rem;
  border-top: 1px solid var(--color-border-light);
}

.no-items {
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  padding: 2rem 1rem;
}

/* スケジュールアイテム */
.schedule-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.schedule-item {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--color-background);
  border-radius: var(--radius-md);
  border-left: 4px solid var(--primary-color);
  transition: all var(--transition-base);
}

.schedule-item.menu-open {
  position: relative;
  z-index: 20;
}

.schedule-item:hover {
  background: var(--color-surface);
  box-shadow: var(--shadow-sm);
  transform: translateX(4px);
}

.item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
}

.category-icon {
  font-size: 2rem;
  line-height: 1;
}

.item-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}

.action-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-base);
  background: var(--color-surface);
}

.checklist-btn:hover {
  background: #10b981;
  transform: scale(1.1);
}

.copy-btn:hover {
  background: #f59e0b;
  transform: scale(1.1);
}

.edit-btn:hover {
  background: #3b82f6;
  transform: scale(1.1);
}

.delete-btn:hover {
  background: #ef4444;
  transform: scale(1.1);
}

/* タブコンテンツ */
.tabs-content {
  padding: 0;
  background: var(--color-surface);
  border-radius: 0 0 var(--radius-xl) var(--radius-xl);
}

.day-content {
  animation: fadeIn 0.3s ease-in-out;
  padding: 1rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.day-header-bar {
  display: flex;
  position: relative;
  z-index: 70;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #b4cf9e 0%, #c5dcb3 100%);
  border-radius: var(--radius-lg);
  gap: 0.5rem;
}

.day-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: white;
  flex: 1;
  min-width: 0;
}

.day-date-label {
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 日程ヘッダー内のボタンスタイル */
.day-header-bar .btn-primary {
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: white;
  white-space: nowrap;
  flex-shrink: 0;
}

.day-header-bar .btn-primary:hover {
  background: rgba(255, 255, 255, 0.4);
}

.btn-add-label {
  display: none;
}

.no-items {
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  padding: 3rem 1rem;
  background: var(--color-background);
  border-radius: var(--radius-md);
}

/* スケジュールアイテム */
.schedule-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.schedule-item {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 40px; /* mobile */
  gap: 0.75rem; /* mobile */
  align-items: start;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 1.25rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-lg);
  border-left: 4px solid var(--primary-color);
  transition: all var(--transition-base);
}

.schedule-item:hover {
  background: var(--color-surface);
  box-shadow: var(--shadow-md);
  transform: translateX(4px);
}

.schedule-item.current-live {
  background: #f0fdf4;
  border-color: #86efac;
  border-left-color: #16a34a;
  box-shadow: 0 8px 20px rgba(22, 163, 74, 0.14);
}

.schedule-item.current-live:hover {
  background: #ecfdf5;
}

.item-pin-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  grid-column: 1;
  grid-row: 1;
  width: 30px;
  height: 30px;
  color: #ffffff;
  background: #b4cf9e;
  border: 2px solid #ffffff;
  border-radius: 999px;
  box-shadow: 0 4px 10px rgba(180, 207, 158, 0.28);
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1;
}

.item-icon {
  display: none;
  align-items: flex-start;
  justify-content: center;
  grid-column: 2;
  grid-row: 1;
  min-width: 0;
}

.category-icon {
  font-size: 2rem;
  line-height: 1;
  color: var(--primary-color);
}

.item-time {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  grid-column: 2; /* mobile */
  grid-row: 1; /* mobile */
  justify-content: flex-start; /* mobile */
  margin-bottom: 0.5rem; /* mobile */
  min-width: 0;
}

.end-time-label {
  display: inline-flex;
  align-items: center;
  min-height: auto;
  margin-top: 0.25rem;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.time-badge {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  box-sizing: border-box;
  gap: 0.25rem;
  padding: 0.5rem 0.625rem;
  background: var(--color-surface);
  border-radius: var(--radius-md);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text);
  border: 1px solid var(--color-border-light);
  white-space: nowrap;
  max-width: 100%;
}

.continuation-time-badge {
  width: 128px;
  min-width: 128px;
  min-height: 38px;
  justify-content: flex-start;
  padding: 0.45rem 0.6rem;
  color: var(--color-text);
  white-space: normal;
}

.item-content {
  flex: 1;
  min-width: 0;
  grid-column: 1 / -1; /* mobile */
  grid-row: 2; /* mobile */
}

.item-title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.item-title {
  min-width: 0;
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
}

.direct-edit-input {
  min-width: 0;
  min-height: 38px;
  padding: 0.45rem 0.6rem;
  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  font: inherit;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}

.direct-edit-input:hover {
  border-color: var(--color-border);
}

.direct-edit-input:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(180, 207, 158, 0.2);
  outline: none;
}

.direct-time-input {
  width: 128px;
  min-width: 128px;
  padding-right: 0.35rem;
  font-size: 0.8125rem;
  font-weight: 600;
}

.direct-title-input {
  width: 100%;
  min-width: 0;
  margin: 0;
  font-weight: 700;
}

.inline-edit-error {
  margin: 0 0 0.5rem;
  color: var(--color-danger);
  font-size: 0.75rem;
}

.item-description {
  font-size: 0.875rem;
  color: var(--color-text-muted);
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
}

.item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.status-cost-row {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
}

.meta-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--color-text-muted);
}

.status-cost-row .meta-tag {
  min-height: 32px;
}

.reservation-tag {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #059669;
}

.live-current-tag {
  background: #dcfce7;
  border-color: #86efac;
  color: #166534;
  font-weight: 800;
}

.reservation-pending-tag {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #c2410c;
}

.reservation-booking-link {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
  font-weight: 700;
}

.reservation-booking-link:hover {
  background: #2563eb;
  border-color: #2563eb;
  color: white;
}

.reservation-preparing-tag {
  background: var(--color-background);
  border-color: #d1d5db;
  color: var(--color-text-muted);
  font-weight: 700;
}

.reservation-provider-strip {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.375rem;
  width: 100%;
  margin-top: 0.25rem;
}

.reservation-provider-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
  padding: 0.45rem 0.625rem;
  background: #f8fafc;
  border: 1px solid #dbeafe;
  border-radius: var(--radius-md);
  color: var(--color-text);
  text-decoration: none;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
}

.reservation-provider-chip:hover {
  border-color: #93c5fd;
  box-shadow: var(--shadow-sm);
  transform: translateY(-1px);
}

.reservation-provider-name {
  min-width: 0;
  overflow: hidden;
  font-size: 0.75rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reservation-provider-price {
  flex-shrink: 0;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 800;
}

.not-needed-tag {
  background: var(--color-background);
  border-color: #d1d5db;
  color: var(--color-text-muted);
}

.members-avatars {
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  padding-left: 4px;
}

.member-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid white;
  overflow: hidden;
  margin-left: -4px;
}

.member-avatar:first-child {
  margin-left: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: var(--color-cool-bg);
  color: var(--text-tertiary);
}

.cost-tag {
  background: #fef3c7;
  border-color: #fde68a;
  color: #d97706;
}

.meta-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: var(--primary-lightest);
  border: 1px solid var(--color-primary-light);
  border-radius: var(--radius-md);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--primary-color);
  text-decoration: none;
  transition: all var(--transition-base);
}

.meta-link:hover {
  background: var(--primary-color);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.2);
}

.meta-link.map-link {
  min-width: 44px;
  min-height: 44px;
  color: #46623a;
}

.meta-link.map-link:hover,
.meta-link.map-link:focus-visible {
  background: #46623a;
  border-color: #46623a;
  color: #ffffff;
  text-decoration: underline;
}

.reservation-document-link {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #334155;
  max-width: 100%;
}

.reservation-document-link:hover {
  background: #334155;
  border-color: #334155;
  color: white;
}

/* アクションボタン - 2x2グリッド（デスクトップ） */
.item-actions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.375rem;
  align-content: start;
  grid-column: -2 / -1; /* mobile */
  grid-row: 1 / 3; /* mobile */
  position: relative;
  z-index: 20;
}

/* モバイル: デスクトップボタン非表示、ケバブのみ表示 */
.desktop-action {
  display: none !important;
}

.mobile-action {
  display: block;
  grid-column: 1 / -1;
}

.kebab-wrapper {
  position: relative;
}

.kebab-btn {
  position: relative;
  z-index: 20;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text-muted);
  width: 36px;
  height: 36px;
}

.kebab-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  background: var(--color-surface);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 20;
  min-width: 168px;
  overflow: hidden;
}

.kebab-item {
  display: block;
  width: 100%;
  padding: 0.625rem 1rem;
  text-align: left;
  background: none;
  border: none;
  font-size: 0.875rem;
  color: var(--color-text);
  cursor: pointer;
  transition: background 0.15s;
}

.kebab-item:hover {
  background: var(--color-background);
}

.kebab-delete {
  color: var(--color-danger);
}

.action-btn {
  width: 36px;
  height: 36px;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-base);
  background: var(--color-surface);
  color: var(--color-text-muted);
}

.action-btn:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-sm);
}

.action-btn:active {
  transform: scale(0.95);
}

.checklist-btn {
  color: var(--primary-hover);
  border-color: var(--primary-color);
}

.checklist-btn:hover {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-hover);
}

.copy-btn {
  color: var(--primary-hover);
  border-color: var(--primary-color);
}

.copy-btn:hover {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-hover);
}

.edit-btn {
  color: var(--primary-hover);
  border-color: var(--primary-color);
}

.edit-btn:hover {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-hover);
}

.delete-btn {
  color: var(--color-danger);
  border-color: var(--primary-color);
}

.delete-btn:hover {
  background: #ef4444;
  color: white;
  border-color: var(--color-danger);
}

/* フローティングボタン */
.floating-btn {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--primary-hover) 100%
  );
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(180, 207, 158, 0.4),
    0 8px 24px rgba(180, 207, 158, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  z-index: 4000;
}

.floating-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(180, 207, 158, 0.5),
    0 12px 32px rgba(180, 207, 158, 0.3);
}

.floating-btn:active {
  transform: scale(1.05);
}

.floating-btn-icon {
  font-size: 1.25rem;
  line-height: 1;
  color: #000;
}

.floating-btn-icon img {
  filter: brightness(0);
}

/* ローディング・空状態 */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  text-align: center;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--color-border-light);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-icon {
  font-size: 4rem;
  opacity: 0.5;
  color: var(--text-tertiary);
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
}

.empty-description {
  font-size: 0.9375rem;
  color: var(--color-text-muted);
  margin: 0;
}

/* レスポンシブ - モバイルファースト */
@media (min-width: 768px) {
  /* デスクトップ: フィルタートグルボタン非表示 */
  .filter-toggle-btn {
    display: none;
  }

  /* デスクトップ: フィルターをインライン表示 */
  .filter-overlay {
    display: none !important;
  }

  .filters-sidebar {
    position: static;
    width: auto;
    height: auto;
    box-shadow: none;
    display: block;
    background: var(--color-surface);
    border: 1px solid var(--color-border-light);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
  }

  .filters-sidebar-header {
    display: none;
  }

  .filters-container {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem;
  }

  .filter-group {
    min-width: 150px;
    flex: 1;
  }

  .clear-filters-btn {
    width: auto;
    justify-content: flex-start;
  }

  .schedule-item {
    grid-template-columns: 34px minmax(112px, auto) minmax(260px, 1fr) 36px;
    gap: 1rem;
  }

  .item-time {
    grid-column: auto;
    grid-row: auto;
    justify-content: flex-start;
    margin-bottom: 0;
  }

  .item-content {
    grid-column: auto;
    grid-row: auto;
  }

.item-actions {
  grid-column: auto;
  grid-row: auto;
  grid-template-columns: 36px;
  justify-self: end;
  align-self: start;
}

  .desktop-action {
    display: none !important;
  }

  .item-icon {
    grid-column: auto;
    grid-row: auto;
  }

  .item-pin-number {
    grid-column: auto;
    grid-row: auto;
    align-self: start;
  }

  .mobile-action {
    display: block !important;
    grid-column: auto;
  }

  .btn-add-label {
    display: inline;
  }

  .day-title {
    font-size: 1.125rem;
  }

  .day-date-label {
    font-size: 1.125rem;
  }

  .tab-button {
    flex: 0 0 auto;
    min-width: auto;
  }
}

@media (min-width: 1024px) {
  .schedule-view {
    padding: 1.5rem;
  }

  .schedule-page-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(460px, 1fr);
    grid-template-areas:
      "filters filters"
      "schedule map";
    align-items: start;
    gap: 1.5rem;
  }

  .schedule-left-pane {
    display: contents;
  }

  .schedule-container {
    display: contents;
    max-width: none;
    margin: 0;
  }

  .action-bar {
    grid-area: filters;
    margin-bottom: 0;
  }

  .planning-assist-panel {
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.1fr) minmax(320px, 0.9fr);
  }

  .schedule-tabs {
    grid-area: schedule;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: var(--schedule-panel-height);
    overflow: hidden;
  }

  .loading-state,
  .empty-state {
    grid-column: 1 / -1;
  }

  .schedule-map-pane {
    grid-area: map;
    position: sticky;
    top: 1rem;
    height: var(--schedule-panel-height);
  }

  .schedule-map-panel {
    display: flex;
    flex-direction: column;
    height: var(--schedule-panel-height);
    min-height: 0;
  }

  .schedule-map-body {
    flex: 1;
    min-height: 0;
  }

  .schedule-map-body :deep(.map-container),
  .schedule-map-body :deep(.map) {
    height: 100%;
    min-height: 0;
  }

  .route-summary-panel {
    flex-shrink: 0;
  }

  .tabs-header {
    flex-shrink: 0;
  }

  .tabs-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .day-content {
    display: flex;
    height: 100%;
    min-height: 0;
    flex-direction: column;
  }

  .day-header-bar {
    flex-shrink: 0;
  }

  .schedule-day-layout {
    flex: 1;
    min-height: 0;
  }

  .schedule-items {
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    padding-right: 0.25rem;
  }
}

@media (max-width: 640px) {
  .schedule-view {
    padding: 0;
  }

  .schedule-container {
    width: 100%;
    max-width: none;
  }

  .mobile-view-tabs {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
    margin: 0 0.75rem;
    padding: 0.25rem;
    border: 1px solid var(--color-border-light);
    border-radius: var(--radius-lg);
    background: var(--color-background);
  }

  .mobile-view-tab {
    display: inline-flex;
    min-height: 44px;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    padding: 0.625rem 0.75rem;
    border: 0;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--color-text-muted);
    font: inherit;
    font-size: 0.875rem;
    font-weight: 700;
    cursor: pointer;
  }

  .mobile-view-tab.active {
    background: var(--color-surface);
    color: var(--primary-hover);
    box-shadow: var(--shadow-sm);
  }

  .mobile-view-tab:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }

  .schedule-left-pane.mobile-panel-inactive,
  .schedule-map-pane.mobile-panel-inactive {
    display: none;
  }

  .planning-assist-panel {
    padding: 0 0.75rem;
  }

  .template-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .itinerary-draft-card {
    grid-template-columns: 1fr;
    align-items: stretch;
  }

  .draft-card-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .draft-action-btn,
  .draft-icon-btn {
    width: 100%;
  }

  .route-summary-panel {
    align-items: stretch;
    flex-direction: column;
  }

  .route-summary-link {
    width: 100%;
  }

  .tabs-content,
  .day-content {
    padding: 0.75rem;
  }

  .day-header-bar {
    margin-bottom: 0.75rem;
    padding: 0.625rem 0.75rem;
  }

  .schedule-items {
    gap: 0.5rem;
  }

  .schedule-item {
    grid-template-columns: 30px minmax(0, 1fr) 36px;
    gap: 0.5rem;
    padding: 0.75rem;
    border-left-width: 3px;
    border-radius: var(--radius-md);
  }

  .category-icon {
    font-size: 1.375rem;
  }

  .item-pin-number {
    width: 26px;
    height: 26px;
    font-size: 0.72rem;
  }

  .item-time {
    margin-bottom: 0.25rem;
    overflow: hidden;
  }

  .end-time-label {
    margin-top: 0.2rem;
    font-size: 0.75rem;
  }

  .time-badge {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    flex-wrap: wrap;
    white-space: normal;
    line-height: 1.25;
  }

  .item-title-row {
    margin-bottom: 0.25rem;
  }

  .item-title {
    font-size: 0.875rem;
    line-height: 1.35;
  }

  .item-description {
    margin-bottom: 0.5rem;
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .item-meta {
    gap: 0.25rem;
  }

  .status-cost-row {
    gap: 0.25rem;
  }

  .reservation-provider-strip {
    gap: 0.25rem;
  }

  .reservation-provider-chip {
    padding: 0.35rem 0.5rem;
  }

  .meta-tag,
  .meta-link {
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.6875rem;
  }

  .status-cost-row .meta-tag {
    min-height: 26px;
  }
}
</style>
